import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import dayjs from "dayjs";
import { toAppTz, formatDateTime, APP_TIMEZONE } from "../../../Core/utils/dateTime";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DashboardIcon from "@mui/icons-material/Dashboard";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SmsIcon from "@mui/icons-material/Sms";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import {
  useGetAllInvoiceQuery,
  useGetWorkOrderQuery,
  useGetDriverQuery,
  useGetTripSmsLogsQuery,
  useGetCustomerQuery,
  useGetCompanyQuery,
  useGetTripLegsQuery,
  useGetUserQuery,
  useSplitTripLegsMutation,
  useAssignLegDriverMutation,
  useSendTripDriverSmsMutation,
} from "../../../Api/attoDeskApi";
import type { ITripLeg, LegType } from "../../../Api/Interface/api.interface";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import { usePermissions } from "../../../Core/Permissions";
import { TransferType } from "../../../Core/Enum/enum";

// Single source of truth for datetime parsing — see Core/utils/dateTime.ts.
// All stored UTC values get converted to Toronto time before display.
const parseDt = (v: any) => toAppTz(v);

const getTransferTypeLabel = (code: string): string => {
  switch (code) {
    case TransferType.OneWayTransfer: return "One Way Transfer";
    case TransferType.RoundTrip:      return "Round Trip";
    case TransferType.MultiDestination: return "Multi-Destination";
    default: return "";
  }
};

// ─── Types ────────────────────────────────────────────────────────────────────
type Stage    = "all" | "quote" | "invoice" | "confirmed" | "paid" | "unpaid";
type ViewMode = "calendar" | "dispatch";

interface Stop {
  label: "Pickup" | "Stop" | "Drop-off" | "Return Pickup" | "Return Drop-off";
  address: string;
  time: string;
}

interface TripEvent {
  id: string;
  workOrderId: string;
  date: string;
  time: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  service: string;
  stops: Stop[];
  stage: "confirmed" | "paid" | "unpaid";
  grandTotal: number;
  amountPaid: number;
  assignedDriverIds: string[];
  assignedDriverNames: string[];
  quoteNo: string;
  invoiceNo?: string;
  isInvoice: boolean;
  invoiceId: string | number | null;
  paymentStatus: "confirmed" | "paid" | "unpaid";
  rawSale: any;
  busCount: number;
  passengerCount: number;
  transferType: string;
  busType: string;
  isQuote: boolean;
  customerId: string;
  companyId: string;
  companyName: string;
  allSales: { time: string; service: string; busType: string; stops: Stop[] }[];
  // ── Leg-level fields ──────────────────────────────────────────────────────
  legType: LegType;
  legId: string | null;
  legStatus: "unassigned" | "assigned";
  legDriverId: string | null;
  legDriverName: string | null;
  // Raw assign_to JSON from the work order's assign_driver row, kept on
  // the trip so the driver-side filter can match it directly.
  assign_to?: any;
}

// Module-level helper: read an assign_to JSON value (string or array) and
// determine whether it references a given driver id. Tolerates "driverId" /
// "driverID" / "driver_id" / bare "id" key shapes.
const hasDriverInAssignTo = (assignTo: any, driverId: number): boolean => {
  if (!assignTo) return false;
  let arr: any = [];
  try {
    arr = typeof assignTo === "string" ? JSON.parse(assignTo) : assignTo;
  } catch {
    return false;
  }
  if (arr && typeof arr === "object" && !Array.isArray(arr) && Array.isArray((arr as any).drivers)) {
    arr = (arr as any).drivers;
  }
  if (!Array.isArray(arr)) return false;
  const target = Number(driverId);
  return arr.some((d: any) => {
    if (!d || typeof d !== "object") return false;
    return (
      Number((d as any).driverId) === target ||
      Number((d as any).driverID) === target ||
      Number((d as any).driver_id) === target ||
      Number((d as any).id) === target
    );
  });
};

// ─── Config ───────────────────────────────────────────────────────────────────
const STAGE: Record<"confirmed" | "paid" | "unpaid", {
  label: string; color: string; bg: string; border: string; dot: string;
}> = {
  // Invoice colors — bold, high-contrast with solid borders
  confirmed: { label: "Confirmed",  color: "#b45309", bg: "#fef3c7", border: "#f59e0b", dot: "#d97706" },
  paid:      { label: "Fully Paid", color: "#15803d", bg: "#dcfce7", border: "#22c55e", dot: "#16a34a" },
  unpaid:    { label: "Unpaid",     color: "#b91c1c", bg: "#fee2e2", border: "#ef4444", dot: "#dc2626" },
};

const PAST_STYLE  = { label: "Past",  color: "#64748b", bg: "#f1f5f9", border: "#cbd5e1", dot: "#94a3b8" };
// Quotes — muted, low-emphasis (light grey/soft blue, thin border)
const QUOTE_STYLE = { label: "Quote", color: "#64748b", bg: "#f8fafc", border: "#e2e8f0", dot: "#94a3b8" };

// Card colors by leg assignment status (overrides stage colors on the calendar)
const LEG_COLORS: Record<"unassigned" | "assigned", { bg: string; border: string; color: string }> = {
  unassigned: { bg: "#fff1f2", border: "#fecdd3", color: "#be123c" },
  assigned:   { bg: "#f0fdf4", border: "#bbf7d0", color: "#15803d" },
};

// Badge labels and colors per leg type
const LEG_BADGE: Record<LegType, { label: string; bg: string; color: string }> = {
  one_way:  { label: "ONE WAY",  bg: "#dbeafe", color: "#1d4ed8" },
  outbound: { label: "OUTBOUND", bg: "#dcfce7", color: "#15803d" },
  return:   { label: "RETURN",   bg: "#fef3c7", color: "#b45309" },
};

// Dispatch grid constants
const GRID_START_HOUR = 6;
const GRID_END_HOUR   = 23;
const TOTAL_HOURS     = GRID_END_HOUR - GRID_START_HOUR;
const HOUR_PX         = 90;
const DRIVER_COL_PX   = 176;
const ROW_H           = 80;
const TRIP_W          = 150;

const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const cap = (s?: string | null) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

function bestDate(sale: any, wo: any): dayjs.Dayjs | null {
  // Prioritize actual pickup dateTime from the pickups array (new format)
  const firstPickupDate = Array.isArray(sale?.pickups) && sale.pickups.length > 0
    ? sale.pickups[0]?.dateTime : null;
  for (const v of [
    firstPickupDate,
    sale?.departureDateTime,
    sale?.pickupDateTime,
    sale?.dateTime,
    sale?.date,
    wo?.quoteWorkOrderDueDate,
  ]) {
    if (!v) continue;
    // Standardized: parse stored UTC ISO and convert to Toronto time
    const d = toAppTz(v);
    if (d && d.isValid()) return d;
  }
  return null;
}

const fmtStopTime = (dt: any) => {
  const d = parseDt(dt);
  return d && d.isValid() ? d.format("MMM D · h:mm A") : "";
};

function buildStops(sale: any): Stop[] {
  const stops: Stop[] = [];
  // Use first pickup dateTime if available (new format), fall back to departureDateTime
  const firstPickup = Array.isArray(sale?.pickups) && sale.pickups.length > 0 ? sale.pickups[0] : null;
  const depDt = firstPickup?.dateTime || sale?.departureDateTime;
  const lastDropoff = Array.isArray(sale?.dropoffs) && sale.dropoffs.length > 0
    ? sale.dropoffs[sale.dropoffs.length - 1] : null;
  const from = sale?.transferTypeFrom || firstPickup?.city || sale?.departureAddress || "";
  const to   = sale?.transferTypeTo   || lastDropoff?.city || sale?.destinationAddress || "";

  // Outbound pickup
  if (from) stops.push({
    label: "Pickup",
    address: firstPickup?.address ? `${firstPickup.address}${firstPickup.city ? `, ${firstPickup.city}` : ""}` : from,
    time: fmtStopTime(depDt),
  });

  // Intermediate stops (extra pickups)
  if (Array.isArray(sale?.pickups) && sale.pickups.length > 1) {
    for (let i = 1; i < sale.pickups.length; i++) {
      const p = sale.pickups[i];
      stops.push({
        label: "Stop",
        address: p.address || p.location || "",
        time: fmtStopTime(p.dateTime),
      });
    }
  }

  // Outbound drop-off(s)
  if (Array.isArray(sale?.dropoffs) && sale.dropoffs.length > 0) {
    sale.dropoffs.forEach((d: any, di: number) => {
      stops.push({
        label: "Drop-off",
        address: d.address ? `${d.address}${d.city ? `, ${d.city}` : ""}` : (di === sale.dropoffs.length - 1 ? to : ""),
        time: fmtStopTime(d.dateTime),
      });
    });
  } else if (to) {
    stops.push({
      label: "Drop-off",
      address: to,
      time: fmtStopTime(lastDropoff?.dateTime || sale?.returnDateTime),
    });
  }

  // Return route is intentionally NOT included here. Round-trip work orders
  // produce a separate "return" TripEvent (see allTrips memo) with its own
  // returnStops array, so the outbound trip's modal shows only outbound
  // pickups/dropoffs and the return trip's modal shows only return ones —
  // no merging across legs.

  return stops;
}

function parseTimeMins(timeStr: string): number | null {
  const m = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return null;
  let h = parseInt(m[1]);
  const min = parseInt(m[2]);
  const ap = m[3].toUpperCase();
  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return h * 60 + min;
}

function tripLeft(time: string): number {
  const mins = parseTimeMins(time);
  if (mins === null) return 0;
  const offsetMins = mins - GRID_START_HOUR * 60;
  return Math.max(0, Math.min((offsetMins / 60) * HOUR_PX, TOTAL_HOURS * HOUR_PX - TRIP_W));
}

function hasTimeConflict(existing: TripEvent[], incoming: TripEvent): boolean {
  const inT = parseTimeMins(incoming.time);
  if (inT === null) return false;
  for (const t of existing) {
    if (t.workOrderId === incoming.workOrderId) continue;
    const exT = parseTimeMins(t.time);
    if (exT === null) continue;
    if (Math.abs(inT - exT) < 90) return true;
  }
  return false;
}

// Parse "HH:mm" (24-hour) → total minutes since midnight
function parseTimeMins24(hhmm: string): number | null {
  const m = (hhmm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return parseInt(m[1]) * 60 + parseInt(m[2]);
}

// Check whether a trip fits inside a driver's availability slots for that day.
// Only considers slots with VALID startTime AND endTime (handles Edit-form all-7-day storage).
// Returns { valid: true } when no valid slots are configured for that day.
function checkDriverAvailability(
  driver: any,
  tripDate: string,       // "YYYY-MM-DD"
  pickupDt: string | null,
  dropoffDt: string | null,
): { valid: boolean; reason: string } {
  const avail: any[] = Array.isArray(driver.availability) ? driver.availability : [];
  if (avail.length === 0) return { valid: true, reason: "" }; // no schedule → allow

  const dayName  = dayjs(tripDate).format("dddd");             // e.g. "Monday"
  const dayAvail = avail.find((a: any) => (a.day || "").toLowerCase() === dayName.toLowerCase());

  // Collect only slots that have both valid times
  const activeSlots = (dayAvail?.timeSlots ?? [])
    .map((s: any) => ({
      startMins: parseTimeMins24(s.startTime || "") ?? -1,
      endMins:   parseTimeMins24(s.endTime   || "") ?? -1,
      label:     `${s.startTime || ""}–${s.endTime || ""}`,
    }))
    .filter((s: any) => s.startMins >= 0 && s.endMins >= 0);

  if (activeSlots.length === 0) return { valid: true, reason: "" }; // no valid slots → allow

  const pickupDjs  = pickupDt  ? dayjs(pickupDt)  : null;
  const dropoffDjs = dropoffDt ? dayjs(dropoffDt) : null;
  const pickupMins  = pickupDjs?.isValid()  ? pickupDjs.hour()  * 60 + pickupDjs.minute()  : null;
  const dropoffMins = dropoffDjs?.isValid() ? dropoffDjs.hour() * 60 + dropoffDjs.minute() : null;

  if (pickupMins === null) return { valid: true, reason: "" }; // no time info → allow

  for (const slot of activeSlots) {
    if (pickupMins >= slot.startMins && (dropoffMins === null || dropoffMins <= slot.endMins)) {
      return { valid: true, reason: "" };
    }
  }

  const slotDescs = activeSlots.map((s: any) => s.label).join(", ");
  return {
    valid: false,
    reason: `Trip time is outside driver's working hours on ${dayName} (${slotDescs})`,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
const BookingCalendar = () => {
  const navigate    = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const today    = dayjs();
  const API_URL  = process.env.REACT_APP_API_URL;

  // ── Role scoping ──────────────────────────────────────────────────────────
  const { roleName } = usePermissions();
  const { data: currentUserData } = useGetUserQuery();
  const isDriver = roleName === "Driver";
  // currentDriverId is resolved below — declared after allDrivers loads.

  // ── View / Calendar state ─────────────────────────────────────────────────
  const [viewMode,       setViewMode]      = useState<ViewMode>("calendar");
  const [currentMonth,   setCurrentMonth]  = useState(today.startOf("month"));
  const [selectedStage,    setSelectedStage]   = useState<Stage>("all");
  const [selectedDriver,   setSelectedDriver]  = useState("");
  const [selectedCustomer, setSelectedCustomer]= useState("");
  const [selectedCompany,  setSelectedCompany] = useState("");
  const [selectedEvent,  setSelectedEvent] = useState<TripEvent | null>(null);
  const [assignDriverIds, setAssignDriverIds] = useState<string[]>([""]);
  const [isAssigning,    setIsAssigning]   = useState(false);

  // ── Leg assign dialog state ───────────────────────────────────────────────
  const [legAssignTrip,    setLegAssignTrip]    = useState<TripEvent | null>(null);
  const [legAssignDriverId, setLegAssignDriverId] = useState<string>("");
  const [isLegAssigning,   setIsLegAssigning]   = useState(false);
  const [sameDriverPrompt, setSameDriverPrompt] = useState<{
    outboundTrip: TripEvent; returnTrip: TripEvent; driverId: string; driverName: string;
  } | null>(null);

  // ── SMS Confirmation modal state ──────────────────────────────────────────
  const [smsConfirmOpen,    setSmsConfirmOpen]    = useState(false);
  const [smsClientEnabled,  setSmsClientEnabled]  = useState(false);
  const [smsDriverEnabled,  setSmsDriverEnabled]  = useState(true);
  const [clientSmsText,     setClientSmsText]     = useState("");
  const [driverSmsText,     setDriverSmsText]     = useState("");

  // ── Per-leg direct driver SMS state ──────────────────────────────────────
  const [legSmsTrip,    setLegSmsTrip]    = useState<TripEvent | null>(null);
  const [legSmsPhone,   setLegSmsPhone]   = useState("");
  const [legSmsMessage, setLegSmsMessage] = useState("");
  const [legSmsSending, setLegSmsSending] = useState(false);

  // ── Quick actions state ───────────────────────────────────────────────────
  const [quickAnchorEl, setQuickAnchorEl] = useState<HTMLElement | null>(null);
  const [quickTrip,     setQuickTrip]     = useState<TripEvent | null>(null);

  // ── Day modal state ───────────────────────────────────────────────────────
  const [dayModal, setDayModal] = useState<{ date: string; trips: TripEvent[] } | null>(null);

  // ── Dispatch state ────────────────────────────────────────────────────────
  const [currentDay,      setCurrentDay]     = useState(today);
  const [draggingTrip,    setDraggingTrip]   = useState<TripEvent | null>(null);
  const [dragOverId,      setDragOverId]     = useState<string | null>(null);
  const [conflictAlert,      setConflictAlert]     = useState<{
    trip: TripEvent; driverId: string; driverName: string;
  } | null>(null);
  const [availabilityAlert,  setAvailabilityAlert] = useState<{
    message: string; onOverride: () => void;
  } | null>(null);
  const [isDragAssigning, setIsDragAssigning] = useState(false);
  const [unassignedOpen,  setUnassignedOpen]  = useState(true);
  const gridScrollRef = useRef<HTMLDivElement>(null);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  // Force refetch on mount so the calendar always reflects the latest pickup
  // times after the Quote/Invoice edit pages update them.
  const { data: workOrderData, refetch: refetchWO  } = useGetWorkOrderQuery(
    { searchText: "", id: "" },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );
  const { data: invoiceData,   refetch: refetchInv } = useGetAllInvoiceQuery(
    { searchText: "", id: "", quoteWorkOrderCustomerId: "", quoteWorkOrderCustomerProjectId: "" },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );
  const { data: driverData }   = useGetDriverQuery({ searchText: "", id: "" });
  const { data: customerData } = useGetCustomerQuery({ searchText: "", id: "", quoteWorkOrderCustomerId: "" });
  const { data: companyData }  = useGetCompanyQuery({ searchText: "", id: "" });
  const { data: legsData, refetch: refetchLegs } = useGetTripLegsQuery(
    {},
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );

  // Refetch trip data whenever the tab/window regains focus so calendar stays in sync
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refetchWO();
        refetchInv();
        refetchLegs();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refetchWO, refetchInv, refetchLegs]);
  const [splitTripLegs]       = useSplitTripLegsMutation();
  const [assignLegDriver]     = useAssignLegDriverMutation();
  const [sendTripDriverSms]   = useSendTripDriverSmsMutation();

  // ── Trip SMS ───────────────────────────────────────────────────────────────
  const { refetch: refetchSmsLogs } = useGetTripSmsLogsQuery(
    { work_order_id: Number(selectedEvent?.workOrderId || 0) },
    { skip: !selectedEvent }
  );

  const allDrivers = useMemo<any[]>(() => {
    if (!Array.isArray(driverData?.data)) return [];
    return driverData!.data.filter((d: any) => d.driverUserRole === "driver");
  }, [driverData]);

  // Resolve the logged-in user's driver id from the drivers list (no name
  // matching, no invoice trust). Compares against drivers.id since that's
  // exactly what assign_to JSON stores: [{ driverId: <drivers.id> }].
  const currentDriverId = useMemo<number | null>(() => {
    const user: any = (currentUserData as any)?.data;
    if (!user || !Array.isArray(allDrivers)) return null;

    const loginEmail = String(user.email || user.userEmail || "").toLowerCase();

    const match = allDrivers.find((d: any) => {
      return (
        String(d.id) === String(user.driver_id) ||
        String(d.id) === String(user.driverId) ||
        String(d.user_id) === String(user.id) ||
        String(d.userId) === String(user.id) ||
        String(d.driverEmail || "").toLowerCase() === loginEmail ||
        // Back-reference: users.user_id may already point at drivers.id
        (user?.user_id && String(d.id) === String(user.user_id))
      );
    });

    return match?.id ? Number(match.id) : null;
  }, [currentUserData, allDrivers]);

  // DEBUG — kept until driver/calendar id resolution is verified end-to-end.
  // Prints once per relevant change so we can see exactly which row matched.
  React.useEffect(() => {
    if (!isDriver) return;
    // eslint-disable-next-line no-console
    console.log("DRIVER DEBUG", {
      loggedUser: (currentUserData as any)?.data,
      allDrivers: (allDrivers || []).map((d: any) => ({
        id: d.id,
        user_id: d.user_id,
        driverEmail: d.driverEmail,
        name: `${d.driverFirstName ?? ""} ${d.driverLastName ?? ""}`.trim(),
      })),
      resolvedCurrentDriverId: currentDriverId,
    });
  }, [isDriver, currentUserData, allDrivers, currentDriverId]);

  const allCustomers = useMemo<any[]>(() => {
    if (!Array.isArray(customerData?.data)) return [];
    return [...customerData!.data].sort((a: any, b: any) => {
      const nameA = (a.customerCompanyName || `${a.customerFirstName || ""} ${a.customerLastName || ""}`.trim()).toLowerCase();
      const nameB = (b.customerCompanyName || `${b.customerFirstName || ""} ${b.customerLastName || ""}`.trim()).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [customerData]);

  const allCompanies = useMemo<any[]>(() => {
    if (!Array.isArray(companyData?.data)) return [];
    return [...companyData!.data].sort((a: any, b: any) =>
      (a.companyName || "").localeCompare(b.companyName || "")
    );
  }, [companyData]);

  const invoiceByWoId = useMemo<Record<string, any>>(() => {
    if (!Array.isArray(invoiceData?.data)) return {};
    const map: Record<string, any> = {};
    for (const inv of invoiceData!.data as any[]) {
      map[String(inv.work_order_id)] = inv;
    }
    return map;
  }, [invoiceData]);

  // ── Index legs by "workOrderId:legType" for fast lookup ──────────────────
  const legMap = useMemo<Record<string, ITripLeg>>(() => {
    if (!Array.isArray(legsData?.data)) return {};
    const map: Record<string, ITripLeg> = {};
    for (const leg of legsData!.data as ITripLeg[]) {
      map[`${leg.work_order_id}:${leg.leg_type}`] = leg;
    }
    return map;
  }, [legsData]);

  // ── Build trip events (one per leg — round trips produce 2 events) ───────
  const allTrips = useMemo<TripEvent[]>(() => {
    const workOrders: any[] = Array.isArray(workOrderData?.data) ? (workOrderData!.data as any[]) : [];
    // Fallback source: when /work-order returns nothing (driver scope edge
    // cases, stale relations, etc.) we still want the calendar to render the
    // trips the dashboard is showing. Synthesize work-order-shaped rows from
    // invoices that aren't already covered.
    const byId: Record<string, any> = {};
    for (const wo of workOrders) byId[String(wo.id)] = wo;
    const invoiceList: any[] = Array.isArray(invoiceData?.data) ? (invoiceData!.data as any[]) : [];
    for (const inv of invoiceList) {
      const woId = inv?.work_order_id;
      if (!woId || byId[String(woId)]) continue;
      // Build a minimal work-order shape from the invoice — the loop below
      // already prefers the invoice as the source-of-truth when present.
      byId[String(woId)] = {
        id: woId,
        quoteWorkOrderNo: inv.quoteWorkOrderNo,
        quoteWorkOrderCustomerId: inv.quoteWorkOrderCustomerId,
        quoteWorkOrderCompanyId: inv.quoteWorkOrderCompanyId,
        isInvoice: true,
        invoice: inv,
        invoiceId: inv.id,
        customer: inv.customer ?? null,
        company: inv.company ?? null,
        project: inv.project ?? null,
        quoteWorkOrderSale: inv.quoteWorkOrderSale ?? [],
        quoteWorkOrderSaleGrandTotal: inv.quoteWorkOrderSaleGrandTotal,
        amount_paid: inv.amount_paid,
        due_amount: inv.due_amount,
        InvoiceNo: inv.InvoiceNo,
        po_number: inv.po_number,
        assignDriver: null, // assignment shape unknown from invoice payload
      };
    }
    const merged = Object.values(byId);
    if (merged.length === 0) return [];
    const trips: TripEvent[] = [];

    for (const wo of merged as any[]) {
      const inv = invoiceByWoId[String(wo.id)] ?? wo.invoice;
      const hasInvoice = !!inv;
      const isQuote    = !(wo.isInvoice === true || wo.isInvoice == 1) && !hasInvoice;

      // SOURCE OF TRUTH: once an invoice exists, the invoice row is authoritative
      // for trip JSON, totals, and payment state. Otherwise use the work order.
      const source: any = hasInvoice ? inv : wo;

      const grandTotal = parseFloat(source.quoteWorkOrderSaleGrandTotal || wo.quoteWorkOrderSaleGrandTotal || "0") || 0;
      const amountPaid = parseFloat(source.amount_paid || wo.amount_paid || "0") || 0;
      const stage: "confirmed" | "paid" | "unpaid" =
        grandTotal > 0 && amountPaid >= grandTotal ? "paid" :
        amountPaid === 0 ? "unpaid" : "confirmed";

      let assignedDrivers: any[] = Array.isArray(inv?.drivers) ? inv.drivers : [];

      // Fallback for quotes (no invoice yet): pull driver IDs from the WO's
      // assign_driver JSON so driver-scoped views still see the assignment
      // even before splitLegs has populated trip_legs.assigned_driver_id.
      if (assignedDrivers.length === 0 && wo.assignDriver?.assign_to) {
        try {
          const raw = wo.assignDriver.assign_to;
          const entries = typeof raw === "string" ? JSON.parse(raw) : raw;
          if (Array.isArray(entries)) {
            const driverIds = entries
              .map((e: any) => e?.driverId)
              .filter((id: any) => id != null);
            assignedDrivers = driverIds.map((id: any) => {
              const dr = allDrivers.find((d: any) => String(d.id) === String(id));
              return dr ?? { id, driverFirstName: "", driverLastName: "" };
            });
          }
        } catch { /* malformed JSON — ignore */ }
      }

      const assignedDriverIds   = assignedDrivers.map((d: any) => String(d.id));
      const assignedDriverNames = assignedDrivers.map((d: any) =>
        `${cap(d.driverFirstName)} ${cap(d.driverLastName)}`.trim()
      );

      const clientName =
        wo.project?.projectName ||
        wo.customer?.customerCompanyName ||
        `${cap(wo.customer?.customerFirstName)} ${cap(wo.customer?.customerLastName)}`.trim() || "—";

      const clientPhone = wo.customer?.customerMobileNum || wo.customer?.customerPhone || "";
      const clientEmail = wo.customer?.customerEmail || "";
      // Once converted, prefer invoice's sale JSON (updated pickup/dropoff times).
      const sales: any[] = Array.isArray(source.quoteWorkOrderSale)
        ? source.quoteWorkOrderSale
        : (Array.isArray(wo.quoteWorkOrderSale) ? wo.quoteWorkOrderSale : []);

      // Use first sale as the primary; multi-sale WOs keep existing behaviour.
      const primarySale: any = sales[0] ?? null;
      const isRoundTrip = primarySale?.transferType === TransferType.RoundTrip;

      const busCount = sales.reduce((sum: number, s: any) => sum + (s.busCount ?? 1), 0) || 1;
      const passengerCount = sales.reduce((sum: number, s: any) => sum + (s.numberOfPassengers ?? 0), 0);
      const busType = sales.map((s: any) => s.productShortDescription || "").filter(Boolean).join(", ");

      // ── Outbound / one-way leg ───────────────────────────────────────────
      let outboundDate: dayjs.Dayjs | null = null;
      let outboundSale: any = null;

      if (sales.length === 0) {
        outboundDate = bestDate(null, wo);
      } else {
        for (const sale of sales) {
          const d = bestDate(sale, wo);
          if (d && (!outboundDate || d.isBefore(outboundDate))) {
            outboundDate = d;
            outboundSale = sale;
          }
        }
      }

      if (!outboundDate) continue;

      const outboundLegType: LegType = isRoundTrip ? "outbound" : "one_way";
      const outboundLeg = legMap[`${wo.id}:${outboundLegType}`] ?? null;

      const allSales = sales.length > 0
        ? sales.map((s: any) => {
            const d = bestDate(s, wo);
            return { time: d?.format("h:mm A") || "", service: s.productName || "—", busType: s.productShortDescription || "", stops: buildStops(s) };
          })
        : [{ time: outboundDate.format("h:mm A"), service: "—", busType: "", stops: [] }];

      trips.push({
        id: `wo-${wo.id}-${outboundLegType}`,
        workOrderId: String(wo.id),
        date: outboundDate.format("YYYY-MM-DD"),
        time: outboundDate.format("h:mm A"),
        legType: outboundLegType,
        legId:         outboundLeg ? String(outboundLeg.id) : null,
        legStatus:     outboundLeg?.status ?? "unassigned",
        legDriverId:   outboundLeg?.assigned_driver_id ? String(outboundLeg.assigned_driver_id) : null,
        legDriverName: outboundLeg?.driver
          ? `${cap(outboundLeg.driver.driverFirstName)} ${cap(outboundLeg.driver.driverLastName)}`.trim()
          : null,
        clientName, clientPhone, clientEmail,
        service: allSales.map((s) => s.service).join(" · "),
        stops: outboundSale ? buildStops(outboundSale) : [],
        stage, grandTotal, amountPaid,
        assignedDriverIds, assignedDriverNames,
        quoteNo: wo.quoteWorkOrderNo || "—",
        invoiceNo: inv?.InvoiceNo,
        // Source-of-truth flags so card/popover see invoice status correctly
        isInvoice: hasInvoice,
        invoiceId: inv?.id ?? wo.invoiceId ?? null,
        paymentStatus: stage,
        rawSale: outboundSale,
        busCount, passengerCount,
        transferType: primarySale?.transferType || "",
        busType, isQuote,
        customerId: String(wo.quoteWorkOrderCustomerId || ""),
        companyId:  String(wo.quoteWorkOrderCompanyId  || ""),
        companyName: wo.company?.companyName || "",
        allSales,
        assign_to: wo?.assignDriver?.assign_to ?? null,
      } as any);

      // ── Return leg (round trips only) ────────────────────────────────────
      if (isRoundTrip) {
        const returnPickups  = Array.isArray(primarySale?.returnPickups)  ? primarySale.returnPickups  : [];
        const returnDropoffs = Array.isArray(primarySale?.returnDropoffs) ? primarySale.returnDropoffs : [];
        const firstReturnPickup = returnPickups[0] ?? null;

        const returnDtRaw = firstReturnPickup?.dateTime ?? primarySale?.returnDateTime ?? null;
        // Standardized: parse stored UTC ISO and convert to user's local zone
        const returnDate  = parseDt(returnDtRaw);

        if (returnDate && returnDate.isValid()) {
          const returnLeg = legMap[`${wo.id}:return`] ?? null;

          const returnStops: Stop[] = [
            ...returnPickups.map((rp: any): Stop => ({
              label:   "Return Pickup",
              address: rp.address ? `${rp.address}${rp.city ? `, ${rp.city}` : ""}` : (rp.city || ""),
              time:    fmtStopTime(rp.dateTime),
            })),
            ...returnDropoffs.map((rd: any): Stop => ({
              label:   "Return Drop-off",
              address: rd.address ? `${rd.address}${rd.city ? `, ${rd.city}` : ""}` : (rd.city || ""),
              time:    fmtStopTime(rd.dateTime),
            })),
          ];

          trips.push({
            id: `wo-${wo.id}-return`,
            workOrderId: String(wo.id),
            date: returnDate.format("YYYY-MM-DD"),
            time: returnDate.format("h:mm A"),
            legType: "return",
            legId:         returnLeg ? String(returnLeg.id) : null,
            legStatus:     returnLeg?.status ?? "unassigned",
            legDriverId:   returnLeg?.assigned_driver_id ? String(returnLeg.assigned_driver_id) : null,
            legDriverName: returnLeg?.driver
              ? `${cap(returnLeg.driver.driverFirstName)} ${cap(returnLeg.driver.driverLastName)}`.trim()
              : null,
            clientName, clientPhone, clientEmail,
            service: primarySale?.productName || "Return",
            stops: returnStops,
            stage, grandTotal, amountPaid,
            assignedDriverIds, assignedDriverNames,
            quoteNo: wo.quoteWorkOrderNo || "—",
            invoiceNo: inv?.InvoiceNo,
            // Source-of-truth flags so card/popover see invoice status correctly
            isInvoice: hasInvoice,
            invoiceId: inv?.id ?? wo.invoiceId ?? null,
            paymentStatus: stage,
            rawSale: primarySale,
            busCount, passengerCount,
            transferType: primarySale?.transferType || "",
            busType, isQuote,
            customerId: String(wo.quoteWorkOrderCustomerId || ""),
            companyId:  String(wo.quoteWorkOrderCompanyId  || ""),
            companyName: wo.company?.companyName || "",
            allSales: [{ time: returnDate.format("h:mm A"), service: primarySale?.productName || "Return", busType: primarySale?.productShortDescription || "", stops: returnStops }],
            assign_to: wo?.assignDriver?.assign_to ?? null,
          } as any);
        }
      }
    }
    // Driver scope — only the trip's PRIMARY driver counts.
    // Priority order:
    //   1. TripLeg.assigned_driver_id  (most reliable per-bus signal — set
    //      when admin actually assigns a driver to a leg)
    //   2. First entry in the work-order's assign_to JSON (the primary)
    //   3. First entry in assignedDriverIds[]  (parsed flavour of the same)
    // A multi-driver trip surfaces only on the calendar of whichever driver
    // owns the leg, never on every co-driver's calendar.
    if (isDriver) {
      const id = Number(currentDriverId);
      if (!id) {
        // eslint-disable-next-line no-console
        console.log("DRIVER CAL FILTER", { currentDriverId, before: trips.length, after: 0, reason: "no driver id" });
        return [];
      }
      const primaryFromAssignTo = (assignTo: any): number | null => {
        if (!assignTo) return null;
        let arr: any = assignTo;
        try {
          if (typeof arr === "string") arr = JSON.parse(arr);
        } catch { return null; }
        if (arr && typeof arr === "object" && !Array.isArray(arr) && Array.isArray(arr.drivers)) {
          arr = arr.drivers;
        }
        if (!Array.isArray(arr) || arr.length === 0) return null;
        const first = arr[0];
        if (!first || typeof first !== "object") return null;
        const candidate =
          first.driverId ?? first.driverID ?? first.driver_id ?? first.id ?? null;
        return candidate != null ? Number(candidate) : null;
      };

      const filtered = trips.filter((t: any) => {
        // 1. Per-leg primary
        if (t?.legDriverId != null && Number(t.legDriverId) === id) return true;
        // 2. Primary driver from assign_to JSON
        const primary = primaryFromAssignTo(t?.assign_to);
        if (primary !== null) return primary === id;
        // 3. Fallback: first id in assignedDriverIds[] is the primary
        if (Array.isArray(t?.assignedDriverIds) && t.assignedDriverIds.length > 0) {
          return Number(t.assignedDriverIds[0]) === id;
        }
        return false;
      });
      // eslint-disable-next-line no-console
      console.log("DRIVER CAL FILTER", {
        currentDriverId: id,
        before: trips.length,
        after: filtered.length,
      });
      return filtered;
    }
    return trips;
  }, [workOrderData, invoiceData, invoiceByWoId, legMap, isDriver, currentDriverId, allDrivers]);

  // ── Today stats ───────────────────────────────────────────────────────────
  const todayKey = today.format("YYYY-MM-DD");
  const todayStats = useMemo(() => {
    const todayTrips = allTrips.filter((t) => t.date === todayKey);
    const uniqueDriverIds = new Set(todayTrips.flatMap((t) =>
      t.legDriverId ? [t.legDriverId] : t.assignedDriverIds
    ));
    return {
      total:      todayTrips.length,
      unassigned: todayTrips.filter((t) => t.legStatus === "unassigned").length,
      drivers:    uniqueDriverIds.size,
      completed:  allTrips.filter((t) => dayjs(t.date).isBefore(today, "day") && t.stage === "paid").length,
      issues:     allTrips.filter((t) => !dayjs(t.date).isBefore(today, "day") && t.stage === "unpaid").length,
    };
  }, [allTrips, todayKey, today]);

  // ── Filters (Calendar) ────────────────────────────────────────────────────
  const visibleTrips = useMemo<TripEvent[]>(() => {
    let list = allTrips;
    if (selectedStage === "quote")   list = list.filter((t) => t.isQuote);
    else if (selectedStage === "invoice") list = list.filter((t) => !t.isQuote);
    else if (selectedStage !== "all") list = list.filter((t) => t.stage === selectedStage);
    if (selectedDriver) {
      const dr = allDrivers.find((d: any) => String(d.id) === selectedDriver);
      if (dr) {
        const name = `${cap(dr.driverFirstName)} ${cap(dr.driverLastName)}`.trim();
        list = list.filter((t) =>
          t.legDriverId === selectedDriver ||
          t.legDriverName === name ||
          t.assignedDriverNames.includes(name)
        );
      }
    }
    if (selectedCustomer) list = list.filter((t) => t.customerId === selectedCustomer);
    if (selectedCompany)  list = list.filter((t) => t.companyId  === selectedCompany);
    return list;
  }, [allTrips, selectedStage, selectedDriver, selectedCustomer, selectedCompany, allDrivers]);

  const tripsByDate = useMemo<Record<string, TripEvent[]>>(() => {
    const map: Record<string, TripEvent[]> = {};
    for (const t of visibleTrips) {
      if (!map[t.date]) map[t.date] = [];
      map[t.date].push(t);
    }
    return map;
  }, [visibleTrips]);

  const calendarDays = useMemo<dayjs.Dayjs[]>(() => {
    const s = currentMonth.startOf("month").startOf("week");
    const e = currentMonth.endOf("month").endOf("week");
    const days: dayjs.Dayjs[] = [];
    let c = s;
    while (c.isBefore(e) || c.isSame(e, "day")) { days.push(c); c = c.add(1, "day"); }
    return days;
  }, [currentMonth]);

  const counts = useMemo(() => {
    // Dedupe by unique identifier so multiple legs of the same trip count once
    const isInvoiceTrip = (t: TripEvent) => {
      const a = t as any;
      return (
        a?.isInvoice === true ||
        a?.invoiceId != null ||
        a?.type === "invoice" ||
        t.isQuote === false
      );
    };
    const uniqueInvoiceIds = new Set<string>();
    const uniqueQuoteIds = new Set<string>();
    const uniqueAllIds = new Set<string>();
    const uniqueByStage: Record<string, Set<string>> = {
      confirmed: new Set(),
      paid: new Set(),
      unpaid: new Set(),
    };
    for (const t of allTrips) {
      const a = t as any;
      const invKey = a?.invoiceId != null ? String(a.invoiceId) : t.invoiceNo || null;
      const quoteKey = String(t.workOrderId);
      uniqueAllIds.add(quoteKey);
      if (isInvoiceTrip(t)) {
        uniqueInvoiceIds.add(invKey || quoteKey);
      } else {
        uniqueQuoteIds.add(quoteKey);
      }
      if (uniqueByStage[t.stage]) uniqueByStage[t.stage].add(quoteKey);
    }
    return {
      all:       uniqueAllIds.size,
      quote:     uniqueQuoteIds.size,
      invoice:   uniqueInvoiceIds.size,
      confirmed: uniqueByStage.confirmed.size,
      paid:      uniqueByStage.paid.size,
      unpaid:    uniqueByStage.unpaid.size,
    };
  }, [allTrips]);

  // ── Dispatch memos ────────────────────────────────────────────────────────
  const dayKey = currentDay.format("YYYY-MM-DD");

  const tripsForDay = useMemo<TripEvent[]>(() => {
    let list = allTrips.filter((t) => t.date === dayKey);
    if (selectedStage !== "all") list = list.filter((t) => t.stage === selectedStage);
    return list;
  }, [allTrips, dayKey, selectedStage]);

  const unassignedForDay = useMemo<TripEvent[]>(() =>
    tripsForDay.filter((t) => t.assignedDriverIds.length === 0),
  [tripsForDay]);

  const tripsByDriverId = useMemo<Record<string, TripEvent[]>>(() => {
    const map: Record<string, TripEvent[]> = {};
    for (const dr of allDrivers) {
      map[String(dr.id)] = tripsForDay.filter((t) => t.assignedDriverIds.includes(String(dr.id)));
    }
    return map;
  }, [tripsForDay, allDrivers]);

  const visibleDrivers = useMemo(() => {
    if (!selectedDriver) return allDrivers;
    return allDrivers.filter((d: any) => String(d.id) === selectedDriver);
  }, [allDrivers, selectedDriver]);

  const dispatchDaySummary = useMemo(() => ({
    total:      tripsForDay.length,
    assigned:   tripsForDay.filter((t) => t.assignedDriverIds.length > 0).length,
    unassigned: unassignedForDay.length,
    paid:       tripsForDay.filter((t) => t.stage === "paid").length,
  }), [tripsForDay, unassignedForDay]);

  // ── Quick action handlers ─────────────────────────────────────────────────
  const openQuickAction = useCallback((trip: TripEvent, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const target = event.currentTarget;
    setQuickAnchorEl(null);
    setQuickTrip(null);
    setQuickAnchorEl(target);
    setQuickTrip(trip);
  }, []);

  const closeQuickAction = useCallback(() => {
    setQuickAnchorEl(null);
    setQuickTrip(null);
  }, []);

  // ── Full detail modal open ────────────────────────────────────────────────
  const openEvent = useCallback((trip: TripEvent) => {
    setSelectedEvent(trip);
    // Initialize slots: fill from existing assignments, pad to busCount with ""
    const needed = Math.max(1, trip.busCount || 1);
    const existing = trip.assignedDriverIds.slice(0, needed);
    const slots = [...existing, ...Array(Math.max(0, needed - existing.length)).fill("")];
    setAssignDriverIds(slots);
    setQuickAnchorEl(null);
    setQuickTrip(null);
  }, []);

  // ── Assign driver (modal) — inner doAssign used by both direct + override ─
  const doModalAssign = async (
    event: TripEvent,
    driverIds: string[],
    sendSmsClient: boolean = false,
    sendSmsDriver: boolean = false,
    clientMessage: string = "",
    driverMessage: string = "",
  ) => {
    setIsAssigning(true);
    try {
      const token   = localStorage.getItem("token") || "";
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/assign-driver`, {
        QuoteId: event.workOrderId,
        drivers: driverIds.filter(Boolean).map(id => ({ driverId: Number(id), price: 0, hourPrice: 0 })),
        send_sms_client: sendSmsClient,
        send_sms_driver: sendSmsDriver,
        client_message:  clientMessage,
        driver_message:  driverMessage,
      }, { headers });
      showMessage("Driver assigned successfully");
      await Promise.all([refetchWO(), refetchInv(), refetchLegs()]);
      const names = driverIds.filter(Boolean).map(id => {
        const dr = allDrivers.find((d: any) => String(d.id) === id);
        return dr ? `${cap(dr.driverFirstName)} ${cap(dr.driverLastName)}`.trim() : "";
      }).filter(Boolean);
      setSelectedEvent((prev) =>
        prev ? { ...prev, assignedDriverIds: driverIds.filter(Boolean), assignedDriverNames: names } : null
      );
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to assign driver";
      showErrorMessage(msg);
    } finally {
      setIsAssigning(false);
    }
  };

  // ── Per-leg assign driver ─────────────────────────────────────────────────
  const openLegAssign = useCallback((trip: TripEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    setLegAssignTrip(trip);
    setLegAssignDriverId(trip.legDriverId || "");
  }, []);

  const closeLegAssign = useCallback(() => {
    setLegAssignTrip(null);
    setLegAssignDriverId("");
  }, []);

  // ── Per-leg: open direct driver SMS dialog ────────────────────────────────
  const openLegSms = useCallback((trip: TripEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    const driver = allDrivers.find((d: any) => String(d.id) === trip.legDriverId);
    const phone  = driver?.driverMobileNum || "";
    setLegSmsTrip(trip);
    setLegSmsPhone(phone);
    setLegSmsMessage("");
  }, [allDrivers]);

  const closeLegSms = useCallback(() => {
    setLegSmsTrip(null);
    setLegSmsPhone("");
    setLegSmsMessage("");
  }, []);

  const handleSendLegSms = useCallback(async () => {
    if (!legSmsTrip) return;
    setLegSmsSending(true);
    try {
      await sendTripDriverSms({
        workOrderId: legSmsTrip.workOrderId,
        phone:       legSmsPhone,
        message:     legSmsMessage,
      }).unwrap();
      showMessage("SMS sent successfully.");
      closeLegSms();
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to send SMS.");
    } finally {
      setLegSmsSending(false);
    }
  }, [legSmsTrip, legSmsPhone, legSmsMessage, sendTripDriverSms, closeLegSms]);

  const handleConfirmLegAssign = useCallback(async (force = false) => {
    if (!legAssignTrip || !legAssignDriverId) return;
    setIsLegAssigning(true);
    try {
      // Ensure legs exist for this booking before assigning
      if (!legAssignTrip.legId) {
        await splitTripLegs(legAssignTrip.workOrderId).unwrap();
        // After split, legs exist — refetch to get the IDs
        await refetchLegs();
        // legId still null here; the mutation will be retried once legsData refreshes
        showMessage("Trip legs created. Please click Assign again.");
        setIsLegAssigning(false);
        return;
      }

      await assignLegDriver({
        leg_id:    legAssignTrip.legId,
        driver_id: Number(legAssignDriverId),
        force,
      }).unwrap();

      // Close the assign dialog and show success immediately.
      // invalidatesTags: ["tripLeg"] on the mutation triggers an automatic
      // refetch of legsData → legMap → allTrips → calendar re-render.
      // Do NOT call refetchLegs() here — the double-refetch races with the
      // automatic invalidation-triggered fetch and can throw into the catch.
      showMessage("Driver assigned to leg.");
      closeLegAssign();

      // After assigning outbound, offer to copy driver to return leg (best-effort)
      if (legAssignTrip.legType === "outbound") {
        try {
          const returnTrip = allTrips.find(
            (t) => t.workOrderId === legAssignTrip.workOrderId && t.legType === "return"
          );
          if (returnTrip && returnTrip.legStatus === "unassigned") {
            const dr = allDrivers.find((d: any) => String(d.id) === legAssignDriverId);
            const driverName = dr
              ? `${cap(dr.driverFirstName)} ${cap(dr.driverLastName)}`.trim()
              : "this driver";
            setSameDriverPrompt({
              outboundTrip: legAssignTrip,
              returnTrip,
              driverId: legAssignDriverId,
              driverName,
            });
          }
        } catch {
          // Non-critical: return-leg detection failure should not mask success
        }
      }
    } catch (err: any) {
      if (err?.data?.conflict) {
        // Show conflict and offer override
        const msg = err?.data?.error || "Driver has a conflicting trip.";
        setAvailabilityAlert({
          message: msg + " Override and assign anyway?",
          onOverride: () => {
            setAvailabilityAlert(null);
            handleConfirmLegAssign(true);
          },
        });
      } else {
        showErrorMessage(err?.data?.error || "Failed to assign driver to leg.");
      }
    } finally {
      setIsLegAssigning(false);
    }
  }, [legAssignTrip, legAssignDriverId, allTrips, allDrivers, assignLegDriver, splitTripLegs, refetchLegs, closeLegAssign]);

  const handleSameDriverForReturn = useCallback(async () => {
    if (!sameDriverPrompt) return;
    const { returnTrip, driverId } = sameDriverPrompt;
    setSameDriverPrompt(null);
    if (!returnTrip.legId) {
      showErrorMessage("Return leg not found. Please refresh and try again.");
      return;
    }
    try {
      await assignLegDriver({ leg_id: returnTrip.legId, driver_id: Number(driverId) }).unwrap();
      showMessage("Same driver assigned to return leg.");
      await refetchLegs();
    } catch (err: any) {
      const msg = err?.data?.error || "Could not assign driver to return leg.";
      showErrorMessage(msg);
    }
  }, [sameDriverPrompt, assignLegDriver, refetchLegs]);

  // ── Build pre-filled SMS messages from current trip + selected drivers ──────
  const buildDefaultMessages = useCallback(() => {
    if (!selectedEvent) return { client: "", driver: "" };

    const sale = selectedEvent.rawSale;

    // ── Categorise stops ──────────────────────────────────────────────────
    const pickupStops   = selectedEvent.stops.filter(s => s.label === "Pickup" || s.label === "Stop");
    const dropoffStops  = selectedEvent.stops.filter(s => s.label === "Drop-off");
    const retPickups    = selectedEvent.stops.filter(s => s.label === "Return Pickup");
    const retDropoffs   = selectedEvent.stops.filter(s => s.label === "Return Drop-off");

    const firstPickup  = pickupStops[0];
    const lastDropoff  = dropoffStops[dropoffStops.length - 1];

    const pickupAddr = firstPickup?.address  || sale?.transferTypeFrom || "N/A";
    const dropAddr   = lastDropoff?.address  || sale?.transferTypeTo   || "N/A";
    const pickupTime = firstPickup?.time || selectedEvent.time;
    const dropoffTime = lastDropoff?.time || "N/A";

    // ── Trip duration ─────────────────────────────────────────────────────
    const firstDepRaw = (Array.isArray(sale?.pickups) && sale.pickups[0]?.dateTime)
      || sale?.departureDateTime;
    const lastArrRaw  = (() => {
      if (Array.isArray(sale?.dropoffs) && sale.dropoffs.length > 0)
        return sale.dropoffs[sale.dropoffs.length - 1]?.dateTime;
      return sale?.returnDateTime;
    })();
    let durationLine = "";
    if (firstDepRaw && lastArrRaw) {
      const dep = dayjs(firstDepRaw);
      const arr = dayjs(lastArrRaw);
      if (dep.isValid() && arr.isValid() && arr.isAfter(dep)) {
        const diffMins = arr.diff(dep, "minute");
        const hrs  = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        durationLine = hrs > 0
          ? `Duration: ${hrs} hr${hrs > 1 ? "s" : ""}${mins > 0 ? ` ${mins} min` : ""}`
          : `Duration: ${mins} min`;
      }
    }

    // ── Driver / client info ──────────────────────────────────────────────
    const selectedDriversInfo = assignDriverIds
      .filter(Boolean)
      .map(id => allDrivers.find((d: any) => String(d.id) === id))
      .filter(Boolean);

    const driverNames  = selectedDriversInfo
      .map((d: any) => `${cap(d.driverFirstName)} ${cap(d.driverLastName)}`.trim())
      .join(", ") || "TBA";
    const driverPhones = selectedDriversInfo
      .map((d: any) => d.driverMobileNum || "N/A")
      .join(", ") || "N/A";

    const tripDate = dayjs(selectedEvent.date).format("ddd, MMM D, YYYY");

    // ── Build stop-list lines (multi-stop) ────────────────────────────────
    const fmtStops = (stops: Stop[], label: string): string => {
      if (stops.length === 0) return `${label}: N/A`;
      if (stops.length === 1) return `${label}: ${stops[0].address}${stops[0].time ? ` - ${stops[0].time}` : ""}`;
      return `${label}s:\n` + stops.map((s, i) =>
        `  ${i + 1}. ${s.address}${s.time ? ` - ${s.time}` : ""}`
      ).join("\n");
    };

    // ── Client SMS ────────────────────────────────────────────────────────
    const client =
      `Hi ${selectedEvent.clientName}, your trip on ${tripDate} at ${pickupTime} is confirmed.\n` +
      `Pickup: ${pickupAddr}\n` +
      `Drop-off: ${dropAddr}\n` +
      (durationLine ? `${durationLine}\n` : "") +
      `Driver: ${driverNames}\n` +
      `Contact: ${driverPhones}\n` +
      `Thank you - TJBus`;

    // ── Driver SMS ────────────────────────────────────────────────────────
    // Return pickup/drop-off are treated as a separate trip and intentionally
    // omitted from this template.
    const driverLines =
      `New Trip Assigned\n\n` +
      `Date: ${tripDate}\n` +
      `Pickup Time: ${pickupTime}\n` +
      `Drop-off Time: ${dropoffTime}\n\n` +
      `Pickup: ${pickupAddr}\n` +
      `Drop-off: ${dropAddr}\n\n` +
      `Client: ${selectedEvent.clientName}\n` +
      `Passengers: ${selectedEvent.passengerCount || "N/A"}\n\n` +
      `Please arrive 10 mins before pickup time.`;

    return { client, driver: driverLines };
  }, [selectedEvent, assignDriverIds, allDrivers]);

  // ── Open SMS confirmation modal (called after validation passes) ─────────
  const openSmsConfirmModal = useCallback(() => {
    const msgs = buildDefaultMessages();
    setClientSmsText(msgs.client);
    setDriverSmsText(msgs.driver);
    setSmsClientEnabled(false);
    setSmsDriverEnabled(true);
    setSmsConfirmOpen(true);
  }, [buildDefaultMessages]);

  // ── Validate drivers → open SMS modal ────────────────────────────────────
  const handleAssignDriver = async () => {
    if (!selectedEvent) return;
    const filled = assignDriverIds.filter(Boolean);
    // Partial assignments are allowed — at least one driver must be picked,
    // remaining buses can be filled later.
    if (filled.length < 1) {
      showErrorMessage("Please select at least one driver to assign");
      return;
    }
    const unique = new Set(filled);
    if (unique.size < filled.length) {
      showErrorMessage("Cannot assign the same driver twice");
      return;
    }
    // availability check for each driver
    for (const driverId of filled) {
      const driver = allDrivers.find((d: any) => String(d.id) === driverId);
      if (driver) {
        const sale = selectedEvent.rawSale;
        const check = checkDriverAvailability(driver, selectedEvent.date, sale?.departureDateTime ?? null, sale?.returnDateTime ?? null);
        if (!check.valid) {
          setAvailabilityAlert({
            message: check.reason,
            onOverride: () => { setAvailabilityAlert(null); openSmsConfirmModal(); },
          });
          return;
        }
      }
    }
    openSmsConfirmModal();
  };

  // ── Full unassign — clears every driver from the trip ─────────────────────
  // Posts drivers: [] so the backend wipes assign_to, clears trip_legs.
  // assigned_driver_id, sends a cancellation SMS to whoever was assigned.
  const handleUnassignAll = async () => {
    if (!selectedEvent) return;
    if (!window.confirm("Remove ALL assigned drivers from this trip?")) return;
    setIsAssigning(true);
    try {
      const token   = localStorage.getItem("token") || "";
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/assign-driver`, {
        QuoteId: selectedEvent.workOrderId,
        drivers: [],
      }, { headers });
      showMessage("All drivers unassigned");
      await Promise.all([refetchWO(), refetchInv(), refetchLegs()]);
      // Clear local slot state and selection so the UI reflects the change
      setAssignDriverIds(Array(Math.max(1, selectedEvent.busCount || 1)).fill(""));
      setSelectedEvent((prev) =>
        prev ? { ...prev, assignedDriverIds: [], assignedDriverNames: [], legDriverId: null, legDriverName: null, legStatus: "unassigned" } : null
      );
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || "Failed to unassign drivers";
      showErrorMessage(msg);
    } finally {
      setIsAssigning(false);
    }
  };

  // ── Confirm: assign drivers + optionally send SMS ─────────────────────────
  const handleConfirmAssign = async () => {
    if (!selectedEvent) return;
    const filled = assignDriverIds.filter(Boolean);
    await doModalAssign(
      selectedEvent,
      filled,
      smsClientEnabled,
      smsDriverEnabled,
      smsClientEnabled ? clientSmsText : "",
      smsDriverEnabled ? driverSmsText : "",
    );
    setSmsConfirmOpen(false);
    if (smsClientEnabled || smsDriverEnabled) refetchSmsLogs();
  };

  // ── Drag & drop assign (dispatch board) ───────────────────────────────────
  const doAssignByDrag = async (workOrderId: string, driverId: string) => {
    setIsDragAssigning(true);
    try {
      const token   = localStorage.getItem("token") || "";
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/assign-driver`, {
        QuoteId: workOrderId,
        drivers: [{ driverId: Number(driverId), price: 0, hourPrice: 0 }],
      }, { headers });
      showMessage("Driver assigned");
      await Promise.all([refetchWO(), refetchInv(), refetchLegs()]);
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to assign driver";
      showErrorMessage(msg);
    } finally {
      setIsDragAssigning(false);
    }
  };

  const handleDragStart = useCallback((e: React.DragEvent, trip: TripEvent) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("tripId",      trip.id);
    e.dataTransfer.setData("workOrderId", trip.workOrderId);
    setDraggingTrip(trip);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingTrip(null);
    setDragOverId(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(targetId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetDriverId: string) => {
    e.preventDefault();
    setDragOverId(null);
    const workOrderId = e.dataTransfer.getData("workOrderId");
    if (!workOrderId || !draggingTrip) return;
    if (draggingTrip.assignedDriverIds.includes(targetDriverId)) return;

    const dr     = allDrivers.find((d: any) => String(d.id) === targetDriverId);
    const drName = dr ? `${cap(dr.driverFirstName)} ${cap(dr.driverLastName)}`.trim() : "this driver";

    // 1. Schedule-conflict check (90-min window)
    const driverTrips = tripsByDriverId[targetDriverId] || [];
    if (hasTimeConflict(driverTrips, draggingTrip)) {
      setConflictAlert({ trip: draggingTrip, driverId: targetDriverId, driverName: drName });
      return;
    }

    // 2. Working-hours check
    if (dr) {
      const sale  = draggingTrip.rawSale;
      const check = checkDriverAvailability(
        dr,
        draggingTrip.date,
        sale?.departureDateTime ?? null,
        sale?.returnDateTime    ?? null,
      );
      if (!check.valid) {
        setAvailabilityAlert({
          message:    check.reason,
          onOverride: () => { setAvailabilityAlert(null); doAssignByDrag(workOrderId, targetDriverId); },
        });
        return;
      }
    }

    doAssignByDrag(workOrderId, targetDriverId);
  }, [draggingTrip, tripsByDriverId, allDrivers]);

  const handleConflictOverride = () => {
    if (!conflictAlert) return;
    doAssignByDrag(conflictAlert.trip.workOrderId, conflictAlert.driverId);
    setConflictAlert(null);
  };

  // ── Filter tabs ───────────────────────────────────────────────────────────
  const TABS: { key: Stage; label: string; color?: string; dot?: string }[] = isDriver
    ? [
        // Drivers only filter by All vs upcoming/past — financial state is
        // intentionally hidden because they shouldn't see paid/unpaid info.
        { key: "all", label: "All" },
      ]
    : [
        { key: "all",       label: "All" },
        { key: "quote",     label: "Quotes",     color: "#2f3891", dot: "#2f3891" },
        { key: "invoice",   label: "Invoices",   color: "#0369a1", dot: "#3b82f6" },
        { key: "confirmed", label: "Confirmed",  color: STAGE.confirmed.color, dot: STAGE.confirmed.dot },
        { key: "paid",      label: "Fully Paid", color: STAGE.paid.color,      dot: STAGE.paid.dot },
        { key: "unpaid",    label: "Unpaid",     color: STAGE.unpaid.color,    dot: STAGE.unpaid.dot },
      ];

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: 2.5,
            background: "linear-gradient(135deg,#2f3891,#2563eb)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
          }}>
            <DirectionsBusIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#0f172a", lineHeight: 1.1 }}>
              {viewMode === "calendar" ? "Booking Calendar" : "Dispatch Board"}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.3 }}>
              Manage trips, assign drivers, and track daily operations
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            startIcon={<TodayIcon sx={{ fontSize: 15 }} />}
            onClick={() => { setCurrentMonth(today.startOf("month")); setCurrentDay(today); }}
            sx={{
              fontSize: 12.5, fontWeight: 700, borderRadius: 2, textTransform: "none",
              color: "#2f3891", border: "1.5px solid #e2e8f0", px: 1.75, height: 36,
              bgcolor: "#fff", "&:hover": { bgcolor: "#eef2ff", borderColor: "#2f3891" },
            }}>
            Today
          </Button>

          <Box sx={{ display: "flex", border: "1.5px solid #e2e8f0", borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}>
            {([
              { mode: "calendar" as ViewMode, Icon: CalendarMonthIcon, label: "Calendar" },
              { mode: "dispatch" as ViewMode, Icon: DashboardIcon,     label: "Dispatch" },
            ]).map(({ mode, Icon, label }) => (
              <Box key={mode} onClick={() => setViewMode(mode)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1.75, py: 0.875, cursor: "pointer",
                  bgcolor: viewMode === mode ? "#2f3891" : "transparent",
                  transition: "all 0.15s",
                  "&:hover": { bgcolor: viewMode === mode ? "#2f3891" : "#f1f5f9" },
                }}>
                <Icon sx={{ fontSize: 15, color: viewMode === mode ? "#fff" : "#64748b" }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: viewMode === mode ? "#fff" : "#64748b" }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Summary Bar ───────────────────────────────────────────────────── */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: isDriver
          ? { xs: "repeat(2,1fr)", sm: "repeat(3,1fr)" }
          : { xs: "repeat(2,1fr)", sm: "repeat(3,1fr)", md: "repeat(5,1fr)" },
        gap: 1.5, mb: 2.5,
      }}>
        {(([
          { label: "Total Today",      value: todayStats.total,      icon: <EventAvailableIcon sx={{ fontSize: 20 }} />,       color: "#2f3891", bg: "#eef2ff",  onClick: () => setCurrentMonth(today.startOf("month")) },
          !isDriver && { label: "Unassigned Today", value: todayStats.unassigned, icon: <PersonAddIcon sx={{ fontSize: 20 }} />,            color: "#d97706", bg: "#fffbeb",  onClick: () => { setSelectedStage("all"); setCurrentMonth(today.startOf("month")); } },
          !isDriver && { label: "Drivers Active",   value: todayStats.drivers,    icon: <PeopleAltIcon sx={{ fontSize: 20 }} />,            color: "#0ea5e9", bg: "#f0f9ff",  onClick: () => {} },
          { label: "Completed",        value: todayStats.completed,  icon: <AssignmentTurnedInIcon sx={{ fontSize: 20 }} />,   color: "#16a34a", bg: "#f0fdf4",  onClick: () => setSelectedStage("paid") },
          { label: "Issues / Unpaid",  value: todayStats.issues,     icon: <ErrorOutlineIcon sx={{ fontSize: 20 }} />,         color: "#dc2626", bg: "#fef2f2",  onClick: () => setSelectedStage("unpaid") },
        ].filter(Boolean)) as { label: string; value: number; icon: React.ReactNode; color: string; bg: string; onClick: () => void }[]).map(
          ({ label, value, icon, color, bg, onClick }) => (
            <Paper key={label} elevation={0} onClick={onClick}
              sx={{
                p: 2, borderRadius: 2.5, border: "1px solid #e2e8f0",
                display: "flex", alignItems: "center", gap: 1.5,
                cursor: "pointer", transition: "all 0.18s",
                "&:hover": {
                  boxShadow: `0 6px 20px ${color}22`,
                  borderColor: `${color}55`,
                  transform: "translateY(-2px)",
                },
              }}>
              <Box sx={{
                width: 46, height: 46, borderRadius: 2, bgcolor: bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                color, flexShrink: 0,
              }}>
                {icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                  {value}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.3, lineHeight: 1.2 }}>
                  {label}
                </Typography>
              </Box>
            </Paper>
          )
        )}
      </Box>

      {/* ── Shared Filters ────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {TABS.map(({ key, label, color, dot }) => {
            const active = selectedStage === key;
            return (
              <Box key={key} onClick={() => setSelectedStage(key)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1.5, py: 0.625, borderRadius: 2, cursor: "pointer",
                  border: active ? `1.5px solid ${color || "#2f3891"}` : "1.5px solid #e2e8f0",
                  bgcolor: active ? (color ? `${color}18` : "#eef2ff") : "#fff",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: color || "#2f3891" },
                }}>
                {dot && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dot }} />}
                <Typography sx={{ fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? (color || "#2f3891") : "#475569" }}>
                  {label}
                </Typography>
                <Box sx={{ px: 0.75, borderRadius: 10, bgcolor: active ? (color || "#2f3891") : "#f1f5f9" }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: active ? "#fff" : "#64748b" }}>
                    {counts[key]}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {!isDriver && (
          <Box sx={{ ml: "auto", display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/* Customer filter */}
            <Select size="small" value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)} displayEmpty
              sx={{
                fontSize: 13, borderRadius: 2, height: 36, minWidth: 175, bgcolor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891" },
              }}>
              <MenuItem value="" sx={{ fontSize: 13 }}>All Customers</MenuItem>
              {allCustomers.map((c: any) => {
                const name = c.customerCompanyName || `${c.customerFirstName || ""} ${c.customerLastName || ""}`.trim() || `#${c.id}`;
                return <MenuItem key={c.id} value={String(c.id)} sx={{ fontSize: 13 }}>{name}</MenuItem>;
              })}
            </Select>

            {/* Company filter */}
            <Select size="small" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} displayEmpty
              sx={{
                fontSize: 13, borderRadius: 2, height: 36, minWidth: 175, bgcolor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891" },
              }}>
              <MenuItem value="" sx={{ fontSize: 13 }}>All Companies</MenuItem>
              {allCompanies.map((co: any) => (
                <MenuItem key={co.id} value={String(co.id)} sx={{ fontSize: 13 }}>{co.companyName}</MenuItem>
              ))}
            </Select>

            {/* Driver filter */}
            <Select size="small" value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)} displayEmpty
              sx={{
                fontSize: 13, borderRadius: 2, height: 36, minWidth: 175, bgcolor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891" },
              }}>
              <MenuItem value="" sx={{ fontSize: 13 }}>All Drivers</MenuItem>
              {allDrivers.map((dr: any) => (
                <MenuItem key={dr.id} value={String(dr.id)} sx={{ fontSize: 13 }}>
                  {cap(dr.driverFirstName)} {cap(dr.driverLastName)}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>

      {/* ════════════════════════════════════════════════════════════════════
          CALENDAR VIEW
      ════════════════════════════════════════════════════════════════════ */}
      {viewMode === "calendar" && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>

          {/* Month nav + legend */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2, borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton size="small" onClick={() => setCurrentMonth((m) => m.subtract(1, "month"))}
                sx={{ borderRadius: 1.5, border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f1f5f9" } }}>
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Typography sx={{ fontWeight: 700, fontSize: 17, color: "#0f172a", minWidth: 190, textAlign: "center" }}>
                {MONTHS[currentMonth.month()]} {currentMonth.year()}
              </Typography>
              <IconButton size="small" onClick={() => setCurrentMonth((m) => m.add(1, "month"))}
                sx={{ borderRadius: 1.5, border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f1f5f9" } }}>
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Legend */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              {([QUOTE_STYLE, ...Object.values(STAGE), PAST_STYLE]).map((cfg) => (
                <Box key={cfg.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: cfg.bg, border: `1.5px solid ${cfg.border}` }} />
                  <Typography sx={{ fontSize: 11.5, color: "#64748b", fontWeight: 500 }}>{cfg.label}</Typography>
                </Box>
              ))}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: PAST_STYLE.bg, border: `1.5px solid ${PAST_STYLE.border}` }} />
                <Typography sx={{ fontSize: 11.5, color: "#64748b", fontWeight: 500 }}>Past</Typography>
              </Box>
            </Box>
          </Box>

          {/* Day headers */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", borderBottom: "1px solid #f1f5f9", bgcolor: "#f8fafc" }}>
            {DAYS.map((d) => (
              <Box key={d} sx={{ py: 1.25, textAlign: "center" }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>{d}</Typography>
              </Box>
            ))}
          </Box>

          {/* Grid cells */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", bgcolor: "#fff" }}>
            {calendarDays.map((day, i) => {
              const dk       = day.format("YYYY-MM-DD");
              const dayTrips = tripsByDate[dk] || [];
              const inMonth  = day.month() === currentMonth.month();
              const isToday  = day.isSame(today, "day");
              const isPast   = day.isBefore(today, "day");
              const maxShow  = 3;
              const extra    = dayTrips.length - maxShow;

              return (
                <Box key={i}
                  sx={{
                    minHeight: 120,
                    borderRight: (i + 1) % 7 !== 0 ? "1px solid #f1f5f9" : "none",
                    borderBottom: i < calendarDays.length - 7 ? "1px solid #f1f5f9" : "none",
                    p: 0.75,
                    position: "relative",
                    bgcolor: !inMonth
                      ? "#fafafa"
                      : isPast
                      ? "#fcfcfd"
                      : day.day() === 0 || day.day() === 6
                      ? "#fdfcff"
                      : "#fff",
                    transition: "background 0.15s",
                    "&:hover": inMonth ? { bgcolor: "#f8f9ff" } : {},
                    "&:hover .cell-add-btn": { opacity: 1, transform: "scale(1)" },
                  }}>

                  {/* Date number row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{
                      width: 26, height: 26, borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: isToday ? "#2f3891" : "transparent",
                    }}>
                      <Typography sx={{
                        fontSize: 12.5,
                        fontWeight: isToday ? 700 : inMonth ? 500 : 400,
                        color: isToday ? "#fff" : inMonth ? "#1e293b" : "#cbd5e1",
                      }}>
                        {day.date()}
                      </Typography>
                    </Box>

                    {/* Add trip "+" */}
                    {inMonth && (
                      <IconButton
                        className="cell-add-btn"
                        size="small"
                        onClick={() => navigate(`/quote/create?date=${dk}`)}
                        sx={{
                          width: 20, height: 20, p: 0,
                          bgcolor: "#2f3891", color: "#fff",
                          opacity: 0,
                          transform: "scale(0.8)",
                          transition: "opacity 0.15s, transform 0.15s",
                          "&:hover": { bgcolor: "#2f3891", transform: "scale(1.1) !important" },
                          "& .MuiSvgIcon-root": { fontSize: 13 },
                        }}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </Box>

                  {/* Trip cards — one per leg */}
                  {dayTrips.slice(0, maxShow).map((trip) => {
                    const isPastTrip = dayjs(trip.date).isBefore(today, "day");
                    // Unified priority: past → quote → invoice payment color (driver status NEVER overrides)
                    const tripAny = trip as any;
                    const isInvoice =
                      tripAny?.isInvoice === true ||
                      tripAny?.invoiceId != null ||
                      tripAny?.type === "invoice" ||
                      trip.isQuote === false;
                    const paymentStatus = (tripAny?.paymentStatus || trip.stage) as string;
                    const isPaid = paymentStatus === "paid";
                    const invoiceTag = isPaid ? "PAID" : "UNPAID";
                    const cardCfg = isPastTrip
                      ? PAST_STYLE
                      : !isInvoice
                        ? QUOTE_STYLE
                        : (isPaid ? STAGE.paid : STAGE.unpaid);
                    const legCfg  = cardCfg;
                    const stageCfg = cardCfg;
                    const badge = LEG_BADGE[trip.legType];
                    const displayDriver = trip.legDriverName || trip.assignedDriverNames[0] || null;
                    // Explicit invoice payment styles applied directly to top-level card
                    const invoiceCardStyles = isInvoice
                      ? (isPaid
                          ? { backgroundColor: "#ecfdf5", border: "1px solid #22c55e" }
                          : { backgroundColor: "#fef2f2", border: "1px solid #ef4444" })
                      : { backgroundColor: legCfg.bg, border: `1px dashed ${legCfg.border}` };
                    return (
                      <Box
                        key={trip.id}
                        onClick={(e) => openQuickAction(trip, e)}
                        sx={{
                          mb: 0.4, borderRadius: 1.5,
                          ...invoiceCardStyles,
                          cursor: "pointer", overflow: "hidden",
                          opacity: isInvoice ? 1 : 0.78,
                          transition: "all 0.12s",
                          "&:hover": {
                            filter: "brightness(0.96)",
                            boxShadow: `0 2px 8px ${legCfg.border}99`,
                            opacity: 1,
                          },
                        }}>
                        {/* QUOTE/INVOICE tag row */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.75, pt: 0.4, gap: 0.4 }}>
                          <Box sx={{
                            px: 0.6, py: "1px", borderRadius: "4px",
                            fontSize: 7.5, fontWeight: 900, letterSpacing: "0.08em",
                            bgcolor: isInvoice ? (isPaid ? "#22c55e" : "#ef4444") : "#e2e8f0",
                            color: isInvoice ? "#fff" : "#475569",
                          }}>
                            {isInvoice ? invoiceTag : "QUOTE"}
                          </Box>
                          <Box sx={{
                            px: 0.5, py: "1px", borderRadius: "4px",
                            fontSize: 7, fontWeight: 700, letterSpacing: "0.04em",
                            bgcolor: badge.bg, color: badge.color,
                          }}>
                            {badge.label}
                          </Box>
                          {/* Stage dot */}
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: stageCfg.dot, flexShrink: 0 }} />
                        </Box>

                        {/* Client name + driver */}
                        <Box sx={{ px: 0.75, pt: 0.25, pb: 0.25 }}>
                          <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: legCfg.color, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {trip.clientName}
                          </Typography>
                          {(isInvoice || displayDriver) && (
                            <Typography sx={{ fontSize: 9, color: legCfg.color, opacity: 0.8, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {displayDriver
                                ? <><CheckCircleIcon sx={{ fontSize: 8, verticalAlign: "middle", mr: 0.25 }} />{displayDriver}</>
                                : "No driver"
                              }
                            </Typography>
                          )}
                          {trip.companyName && (
                            <Box sx={{
                              display: "inline-block", mt: 0.2, px: 0.6, py: "1px",
                              borderRadius: "4px", fontSize: 8, fontWeight: 700, letterSpacing: "0.02em",
                              bgcolor: "#e0f2fe", color: "#0369a1",
                              maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {trip.companyName}
                            </Box>
                          )}
                        </Box>

                        {/* Time + bus type sub-row */}
                        <Box sx={{ px: 0.5, pb: 0.25 }}>
                          {trip.allSales.slice(0, 1).map((sale, si) => (
                            <Box key={si} sx={{
                              bgcolor: isInvoice ? "transparent" : "rgba(255,255,255,0.55)",
                              border: `1px solid ${isInvoice ? (isPaid ? "#22c55e" : "#ef4444") : legCfg.border}`,
                              borderRadius: 1, px: 0.6, py: 0.2,
                            }}>
                              <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: legCfg.color, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {sale.time} · {sale.service}
                              </Typography>
                              {sale.busType && (
                                <Typography sx={{ fontSize: 8.5, color: legCfg.color, opacity: 0.7, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {sale.busType}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>

                        {/* Assign / Reassign / SMS buttons intentionally removed
                            from the calendar card. These actions live inside
                            the detail modal that opens on click. */}
                      </Box>
                    );
                  })}

                  {extra > 0 && (
                    <Box
                      onClick={() => setDayModal({ date: dk, trips: dayTrips })}
                      sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 0.5, cursor: "pointer", "&:hover .more-txt": { textDecoration: "underline" } }}>
                      <Typography className="more-txt" sx={{ fontSize: 10.5, fontWeight: 700, color: "#2f3891" }}>
                        +{extra} more
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          DISPATCH BOARD VIEW
      ════════════════════════════════════════════════════════════════════ */}
      {viewMode === "dispatch" && (
        <Box>
          {isMobile ? (
            <DispatchMobileList tripsForDay={tripsForDay} allDrivers={allDrivers} onOpenEvent={openEvent} />
          ) : (
            <>
              {/* Day nav */}
              <Paper elevation={0} sx={{ borderRadius: 2.5, border: "1px solid #e2e8f0", p: 1.75, mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton size="small" onClick={() => setCurrentDay((d) => d.subtract(1, "day"))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, "&:hover": { bgcolor: "#f1f5f9" } }}>
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Box sx={{ textAlign: "center", minWidth: 220 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
                      {currentDay.format("dddd, MMMM D, YYYY")}
                    </Typography>
                    {currentDay.isSame(today, "day") && (
                      <Typography sx={{ fontSize: 11, color: "#2f3891", fontWeight: 600 }}>Today</Typography>
                    )}
                  </Box>
                  <IconButton size="small" onClick={() => setCurrentDay((d) => d.add(1, "day"))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, "&:hover": { bgcolor: "#f1f5f9" } }}>
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  {!currentDay.isSame(today, "day") && (
                    <Button size="small" startIcon={<TodayIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setCurrentDay(today)}
                      sx={{ fontSize: 12, fontWeight: 700, borderRadius: 2, textTransform: "none", color: "#2f3891", border: "1px solid #e2e8f0", px: 1.5, "&:hover": { bgcolor: "#eef2ff", borderColor: "#2f3891" } }}>
                      Today
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  {[
                    { label: "Total",      val: dispatchDaySummary.total,      color: "#2f3891" },
                    { label: "Assigned",   val: dispatchDaySummary.assigned,   color: "#16a34a" },
                    { label: "Unassigned", val: dispatchDaySummary.unassigned, color: "#d97706" },
                    { label: "Paid",       val: dispatchDaySummary.paid,       color: "#0ea5e9" },
                  ].map(({ label, val, color }) => (
                    <Box key={label} sx={{ px: 1.25, py: 0.375, bgcolor: `${color}12`, border: `1px solid ${color}30`, borderRadius: 10 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color }}>
                        {val} {label}
                      </Typography>
                    </Box>
                  ))}
                  {isDragAssigning && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CircularProgress size={14} sx={{ color: "#2f3891" }} />
                      <Typography sx={{ fontSize: 11, color: "#2f3891", fontWeight: 600 }}>Assigning…</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* Unassigned Trips Panel */}
              <Paper elevation={0} sx={{ borderRadius: 2.5, border: `1.5px solid ${unassignedForDay.length > 0 ? "#fcd34d" : "#e2e8f0"}`, mb: 2, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
                <Box onClick={() => setUnassignedOpen((o) => !o)}
                  sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.25, cursor: "pointer", bgcolor: unassignedForDay.length > 0 ? "#fffbeb" : "#f8fafc", "&:hover": { bgcolor: unassignedForDay.length > 0 ? "#fef9c3" : "#f1f5f9" }, transition: "background 0.15s" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: unassignedForDay.length > 0 ? "#f59e0b" : "#94a3b8", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: unassignedForDay.length > 0 ? "#92400e" : "#475569" }}>
                    Unassigned Trips
                  </Typography>
                  <Box sx={{ px: 0.875, borderRadius: 10, bgcolor: unassignedForDay.length > 0 ? "#f59e0b" : "#94a3b8" }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#fff" }}>{unassignedForDay.length}</Typography>
                  </Box>
                  {unassignedForDay.length > 0 && (
                    <Typography sx={{ fontSize: 11, color: "#92400e", fontStyle: "italic" }}>
                      Drag trips to assign a driver →
                    </Typography>
                  )}
                  <UnfoldMoreIcon sx={{ fontSize: 16, color: "#94a3b8", ml: "auto", transform: unassignedOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                </Box>

                <Collapse in={unassignedOpen}>
                  <Box sx={{ px: 2, pb: 1.5, pt: 0.5, bgcolor: "#fffbeb", borderTop: "1px solid #fde68a" }}>
                    {unassignedForDay.length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: "#94a3b8", py: 1, fontStyle: "italic" }}>
                        All trips for this day are assigned.
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", gap: 1.25, flexWrap: "nowrap", overflowX: "auto", py: 0.75, "&::-webkit-scrollbar": { height: 4 }, "&::-webkit-scrollbar-track": { bgcolor: "#fef3c7" }, "&::-webkit-scrollbar-thumb": { bgcolor: "#f59e0b", borderRadius: 4 } }}>
                        {unassignedForDay.map((trip) => (
                          <TripChip
                            key={trip.id}
                            trip={trip}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onClick={(e) => openQuickAction(trip, e)}
                            isDragging={draggingTrip?.id === trip.id}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>

              {/* Dispatch Grid */}
              {tripsForDay.length === 0 ? (
                <Paper elevation={0} sx={{ borderRadius: 2.5, border: "1px solid #e2e8f0", p: 5, textAlign: "center" }}>
                  <DirectionsBusIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#94a3b8" }}>No trips scheduled</Typography>
                  <Typography sx={{ fontSize: 13, color: "#cbd5e1", mt: 0.5 }}>
                    {currentDay.format("MMMM D, YYYY")} has no trips yet.
                  </Typography>
                </Paper>
              ) : (
                <Paper elevation={0} sx={{ borderRadius: 2.5, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                  {/* Time ruler */}
                  <Box sx={{ display: "flex", borderBottom: "1.5px solid #e2e8f0", bgcolor: "#f8fafc", position: "sticky", top: 0, zIndex: 10 }}>
                    <Box sx={{ width: DRIVER_COL_PX, flexShrink: 0, borderRight: "1.5px solid #e2e8f0", px: 2, py: 1.25, display: "flex", alignItems: "center" }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.7 }}>Driver</Typography>
                    </Box>
                    <Box ref={gridScrollRef} sx={{ flex: 1, overflowX: "auto", "&::-webkit-scrollbar": { height: 5 }, "&::-webkit-scrollbar-track": { bgcolor: "#f1f5f9" }, "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 } }}>
                      <Box sx={{ width: TOTAL_HOURS * HOUR_PX, display: "flex" }}>
                        {Array.from({ length: TOTAL_HOURS }, (_, i) => {
                          const hour  = GRID_START_HOUR + i;
                          const label = hour === 12 ? "12 PM" : hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
                          return (
                            <Box key={i} sx={{ width: HOUR_PX, flexShrink: 0, borderRight: "1px solid #f1f5f9", py: 1, pl: 0.75 }}>
                              <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: "#94a3b8" }}>{label}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>

                  {/* Driver rows */}
                  {visibleDrivers.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No drivers found. Add drivers from Team Management.</Typography>
                    </Box>
                  ) : (
                    visibleDrivers.map((dr: any, idx: number) => {
                      const driverId    = String(dr.id);
                      const driverName  = `${cap(dr.driverFirstName)} ${cap(dr.driverLastName)}`.trim();
                      const driverTrips = (tripsByDriverId[driverId] || []).sort((a, b) =>
                        (parseTimeMins(a.time) ?? 0) - (parseTimeMins(b.time) ?? 0)
                      );
                      const isOver = dragOverId === driverId;

                      return (
                        <Box key={driverId}
                          sx={{ display: "flex", borderBottom: idx < visibleDrivers.length - 1 ? "1px solid #f1f5f9" : "none", bgcolor: isOver ? "#eef2ff" : idx % 2 === 0 ? "#fff" : "#fafafa", transition: "background 0.12s" }}
                          onDragOver={(e) => handleDragOver(e, driverId)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, driverId)}>

                          <Box sx={{ width: DRIVER_COL_PX, flexShrink: 0, borderRight: `1.5px solid ${isOver ? "#a5b4fc" : "#e2e8f0"}`, px: 1.5, py: 1, display: "flex", flexDirection: "column", justifyContent: "center", transition: "border-color 0.12s" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box sx={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg,#2f3891,#2563eb)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>
                                  {(dr.driverFirstName?.[0] || "?").toUpperCase()}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#1e293b", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {driverName}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                                  {driverTrips.length} trip{driverTrips.length !== 1 ? "s" : ""}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          <Box sx={{ flex: 1, overflowX: "auto", position: "relative", "&::-webkit-scrollbar": { height: 4 }, "&::-webkit-scrollbar-thumb": { bgcolor: "#e2e8f0", borderRadius: 4 } }}>
                            <Box sx={{ width: TOTAL_HOURS * HOUR_PX, height: ROW_H, position: "relative" }}>
                              {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                                <Box key={i} sx={{ position: "absolute", left: i * HOUR_PX, top: 0, bottom: 0, width: 1, bgcolor: "#f1f5f9" }} />
                              ))}
                              {isOver && draggingTrip && (
                                <Box sx={{ position: "absolute", inset: 4, borderRadius: 2, border: "2px dashed #2f3891", bgcolor: "#eef2ff50", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2f3891" }}>
                                    Drop to assign {draggingTrip.clientName}
                                  </Typography>
                                </Box>
                              )}
                              {driverTrips.map((trip) => (
                                <DispatchTripBlock
                                  key={trip.id}
                                  trip={trip}
                                  left={tripLeft(trip.time)}
                                  rowH={ROW_H}
                                  tripW={TRIP_W}
                                  isDragging={draggingTrip?.id === trip.id}
                                  onDragStart={handleDragStart}
                                  onDragEnd={handleDragEnd}
                                  onClick={(e) => openQuickAction(trip, e)}
                                />
                              ))}
                              {driverTrips.length === 0 && !isOver && (
                                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", pl: 2 }}>
                                  <Typography sx={{ fontSize: 11, color: "#cbd5e1", fontStyle: "italic" }}>
                                    Drag a trip here to assign
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Paper>
              )}
            </>
          )}
        </Box>
      )}

      {/* ── Conflict Warning Dialog ──────────────────────────────────────── */}
      <Dialog open={!!conflictAlert} onClose={() => setConflictAlert(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        {conflictAlert && (
          <>
            <Box sx={{ px: 3, pt: 3, pb: 1.5, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <WarningAmberIcon sx={{ color: "#d97706", fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a", mb: 0.5 }}>Scheduling Conflict</Typography>
                <Typography sx={{ fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
                  <strong>{conflictAlert.driverName}</strong> already has a trip within 90 minutes of{" "}
                  <strong>{conflictAlert.trip.clientName}</strong> at <strong>{conflictAlert.trip.time}</strong>.
                </Typography>
                <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, fontSize: 12, borderRadius: 2 }}>
                  Assigning anyway may cause scheduling issues.
                </Alert>
              </Box>
            </Box>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
              <Button onClick={() => setConflictAlert(null)} variant="outlined"
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600, borderColor: "#e2e8f0", color: "#475569" }}>
                Cancel
              </Button>
              <Button onClick={handleConflictOverride} variant="contained"
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700, bgcolor: "#d97706", "&:hover": { bgcolor: "#b45309" } }}>
                Assign Anyway
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Availability alert dialog ─────────────────────────────────────── */}
      <Dialog
        open={!!availabilityAlert}
        onClose={() => setAvailabilityAlert(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 0, display: "flex", alignItems: "flex-start", gap: 1.75 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            bgcolor: "#fffbeb", border: "1px solid #fde68a",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <WarningAmberIcon sx={{ color: "#d97706", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a", mb: 0.5 }}>
              Outside Working Hours
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: "#475569", lineHeight: 1.6 }}>
              {availabilityAlert?.message}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 1 }}>
              You can still assign this driver by selecting "Assign Anyway" (admin override).
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
          <Button
            onClick={() => setAvailabilityAlert(null)}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600,
              color: "#475569", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f8fafc" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={availabilityAlert?.onOverride}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700,
              bgcolor: "#d97706", "&:hover": { bgcolor: "#b45309" }, minWidth: 130 }}
          >
            Assign Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quick Actions Popover ────────────────────────────────────────── */}
      <Popover
        open={Boolean(quickAnchorEl) && Boolean(quickTrip)}
        anchorEl={quickAnchorEl}
        onClose={closeQuickAction}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 2.5, border: "1px solid #e2e8f0", boxShadow: "0 8px 32px rgba(0,0,0,0.14)", minWidth: 230, overflow: "hidden" } }}>
        {quickTrip && (() => {
          const trip = quickTrip as any;
          const isInvoice =
            trip?.isInvoice === true ||
            trip?.invoiceId != null ||
            trip?.type === "invoice" ||
            quickTrip.isQuote === false;
          const paymentStatus = (trip?.paymentStatus || quickTrip.stage) as string;
          const isPaid = paymentStatus === "paid";
          const invoiceTag = isPaid ? "PAID" : "UNPAID";
          const cfg = !isInvoice
            ? QUOTE_STYLE
            : (isPaid ? STAGE.paid : STAGE.unpaid);
          return (
            <>
              <Box sx={{ px: 2, py: 1.5, bgcolor: cfg.bg, borderBottom: `${isInvoice ? 2 : 1}px ${isInvoice ? "solid" : "dashed"} ${cfg.border}`, opacity: isInvoice ? 1 : 0.95 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.3 }}>
                  <Box sx={{
                    px: 0.75, py: "1px", borderRadius: "4px",
                    fontSize: 9, fontWeight: 900, letterSpacing: "0.08em",
                    bgcolor: isInvoice ? cfg.color : "#e2e8f0",
                    color: isInvoice ? "#fff" : "#475569",
                  }}>
                    {isInvoice ? invoiceTag : "QUOTE"}
                  </Box>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: cfg.dot }} />
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: cfg.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {cfg.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: "#0f172a", lineHeight: 1.2 }}>
                  {quickTrip.clientName}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.2 }}>
                  {quickTrip.time} · {quickTrip.service}
                </Typography>
              </Box>

              <Box sx={{ py: 0.5 }}>
                {(([
                  { icon: <VisibilityIcon sx={{ fontSize: 16 }} />,       label: "View Details",   color: "#1e293b", action: () => openEvent(quickTrip) },
                  // Edit Trip + Assign Driver are admin-only — drivers should
                  // only be able to view their assigned trip details.
                  ...(!isDriver ? [
                    { icon: <EditIcon sx={{ fontSize: 16 }} />,           label: "Edit Trip",      color: "#1e293b", action: () => { navigate(`/work-order/history/${quickTrip.workOrderId}`); closeQuickAction(); } },
                  ] : []),
                  ...(!isDriver && isInvoice ? [
                    { icon: <PersonAddIcon sx={{ fontSize: 16 }} />,      label: "Assign Driver",  color: "#1e293b", action: () => openEvent(quickTrip) },
                  ] : []),
                ]) as { icon: React.ReactNode; label: string; color: string; action: () => void }[]).map(({ icon, label, color, action }) => (
                  <Box key={label} onClick={action}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1, cursor: "pointer", transition: "background 0.1s", "&:hover": { bgcolor: "#f8fafc" } }}>
                    <Box sx={{ color, opacity: 0.75 }}>{icon}</Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          );
        })()}
      </Popover>

      {/* ── Day Trips Modal ──────────────────────────────────────────────── */}
      <Dialog open={Boolean(dayModal)} onClose={() => setDayModal(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "85vh" } }}>
        {dayModal && (
          <>
            <Box sx={{ px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9" }}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>
                  {dayjs(dayModal.date).format("dddd, MMMM D, YYYY")}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
                  {dayModal.trips.length} trip{dayModal.trips.length !== 1 ? "s" : ""} scheduled
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setDayModal(null)} sx={{ color: "#64748b" }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <DialogContent sx={{ p: 2, overflow: "auto" }}>
              {dayModal.trips
                .sort((a, b) => (parseTimeMins(a.time) ?? 0) - (parseTimeMins(b.time) ?? 0))
                .map((trip) => {
                  const isPastTrip = dayjs(trip.date).isBefore(today, "day");
                  const tripAny = trip as any;
                  const isInvoice =
                    tripAny?.isInvoice === true ||
                    tripAny?.invoiceId != null ||
                    tripAny?.type === "invoice" ||
                    trip.isQuote === false;
                  const paymentStatus = (tripAny?.paymentStatus || trip.stage) as string;
                  const isPaid = paymentStatus === "paid";
                  const invoiceTag = isPaid ? "PAID" : "UNPAID";
                  const cfg = isPastTrip
                    ? PAST_STYLE
                    : !isInvoice
                      ? QUOTE_STYLE
                      : (isPaid ? STAGE.paid : STAGE.unpaid);
                  return (
                    <Paper key={trip.id} elevation={0}
                      onClick={(e) => { setDayModal(null); openQuickAction(trip, e as React.MouseEvent<HTMLElement>); }}
                      sx={{
                        mb: 1, p: 1.75, borderRadius: 2,
                        border: isInvoice ? `2px solid ${cfg.border}` : `1px dashed ${cfg.border}`,
                        bgcolor: cfg.bg,
                        opacity: isInvoice ? 1 : 0.78,
                        cursor: "pointer", transition: "all 0.15s",
                        "&:hover": { boxShadow: `0 3px 12px ${cfg.border}88`, transform: "translateX(2px)", opacity: 1 },
                      }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, flexWrap: "wrap" }}>
                        <Typography sx={{
                          fontSize: 12, fontWeight: 800, color: cfg.color,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          letterSpacing: "0.02em",
                        }}>
                          {isInvoice && trip.invoiceNo ? `INV ${trip.invoiceNo}` : trip.quoteNo}
                        </Typography>
                        <Box sx={{
                          px: 0.75, py: "1px", borderRadius: "4px",
                          fontSize: 9, fontWeight: 900, letterSpacing: "0.08em",
                          bgcolor: isInvoice ? cfg.color : "#e2e8f0",
                          color: isInvoice ? "#fff" : "#475569",
                        }}>
                          {isInvoice ? invoiceTag : "QUOTE"}
                        </Box>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.dot, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: cfg.color }}>{trip.time}</Typography>
                        <Chip label={cfg.label} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: cfg.bg, color: cfg.color, border: "none", ml: "auto" }} />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{trip.clientName}</Typography>
                        {trip.companyName && (
                          <Box sx={{
                            display: "inline-flex", alignItems: "center",
                            px: 1, py: "2px", borderRadius: "5px",
                            fontSize: 10, fontWeight: 700, letterSpacing: "0.02em",
                            bgcolor: trip.companyName.toLowerCase().includes("bus line") ? "#1e3a5f" : "#14532d",
                            color: "#fff",
                          }}>
                            {trip.companyName}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 12, color: "#475569" }}>{trip.service}</Typography>
                        {trip.assignedDriverNames.length > 0 ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 13, color: "#16a34a" }} />
                            <Typography sx={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>{trip.assignedDriverNames.join(", ")}</Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <PersonAddIcon sx={{ fontSize: 13, color: "#f59e0b" }} />
                            <Typography sx={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>No driver assigned</Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* ── Trip Detail Modal ────────────────────────────────────────────── */}
      <Dialog open={Boolean(selectedEvent)} onClose={() => setSelectedEvent(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", maxHeight: "90vh" } }}>
        {selectedEvent && (() => {
          const cfg     = STAGE[selectedEvent.stage] || STAGE.confirmed;
          const balance = Math.max(0, selectedEvent.grandTotal - selectedEvent.amountPaid);
          const hasDriver = selectedEvent.assignedDriverIds.length > 0;
          return (
            <>
              <Box sx={{ px: 3, py: 2, bgcolor: cfg.bg, borderBottom: `1px solid ${cfg.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.dot }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: cfg.color }}>{cfg.label}</Typography>
                  <Chip label={selectedEvent.quoteNo} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#fff", color: cfg.color, border: `1px solid ${cfg.border}` }} />
                  {selectedEvent.invoiceNo && (
                    <Chip label={`INV ${selectedEvent.invoiceNo}`} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#fff", color: cfg.color, border: `1px solid ${cfg.border}` }} />
                  )}
                </Box>
                <IconButton size="small" onClick={() => setSelectedEvent(null)} sx={{ color: cfg.color }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              <DialogContent sx={{ p: 0, overflow: "auto" }}>
                <ModalSection title="Client Information" icon={<PersonIcon sx={{ fontSize: 14, color: "#2f3891" }} />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: "2px solid #e2e8f0" }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#475569" }}>
                        {selectedEvent.clientName[0]?.toUpperCase()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{selectedEvent.clientName}</Typography>
                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.25 }}>
                        {selectedEvent.clientPhone && (
                          <Box component="a" href={`tel:${selectedEvent.clientPhone}`} sx={{ fontSize: 12, color: "#2563eb", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                            📞 {selectedEvent.clientPhone}
                          </Box>
                        )}
                        {selectedEvent.clientEmail && (
                          <Box component="a" href={`mailto:${selectedEvent.clientEmail}`} sx={{ fontSize: 12, color: "#2563eb", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                            ✉ {selectedEvent.clientEmail}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </ModalSection>

                <Divider sx={{ borderColor: "#f1f5f9" }} />

                <ModalSection title="Trip Details" icon={<DirectionsBusIcon sx={{ fontSize: 14, color: "#2f3891" }} />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <DirectionsBusIcon sx={{ fontSize: 17, color: "#475569" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Service</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{selectedEvent.service}</Typography>
                    </Box>
                  </Box>
                  {selectedEvent.busType && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <DirectionsBusIcon sx={{ fontSize: 17, color: "#d97706" }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Bus Type</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{selectedEvent.busType}</Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <AccessTimeIcon sx={{ fontSize: 17, color: "#475569" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Date & Time</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
                        {dayjs(selectedEvent.date).format("dddd, MMM D, YYYY")} · {selectedEvent.time}
                      </Typography>
                    </Box>
                  </Box>
                  {/* Trip Type */}
                  {selectedEvent.transferType && getTransferTypeLabel(selectedEvent.transferType) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <DirectionsBusIcon sx={{ fontSize: 17, color: "#475569" }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Trip Type</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{getTransferTypeLabel(selectedEvent.transferType)}</Typography>
                      </Box>
                    </Box>
                  )}
                  {/* Bus Count + Passengers row */}
                  <Box sx={{ display: "flex", gap: 2, mt: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <DirectionsBusIcon sx={{ fontSize: 17, color: "#2563eb" }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Buses</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#2563eb" }}>{selectedEvent.busCount} {selectedEvent.busCount === 1 ? "Bus" : "Buses"}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <PeopleAltIcon sx={{ fontSize: 17, color: "#16a34a" }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Passengers</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#16a34a" }}>
                          {selectedEvent.passengerCount > 0 ? selectedEvent.passengerCount : "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ModalSection>

                <Divider sx={{ borderColor: "#f1f5f9" }} />

                {selectedEvent.stops.length > 0 && (
                  <>
                    <ModalSection title="Route" icon={<LocationOnIcon sx={{ fontSize: 14, color: "#2f3891" }} />}>
                      {/* Duration — pickup → dropoff for THIS leg only */}
                      {(() => {
                        const stops = selectedEvent.stops;
                        const firstTime = stops.find((s: any) => s.label === "Pickup" || s.label === "Return Pickup")?.time;
                        const lastTime  = [...stops].reverse().find((s: any) => s.label === "Drop-off" || s.label === "Return Drop-off")?.time;
                        if (!firstTime || !lastTime) return null;
                        const startMins = parseTimeMins(firstTime);
                        const endMins   = parseTimeMins(lastTime);
                        if (startMins == null || endMins == null) return null;
                        let diffMins = endMins - startMins;
                        if (diffMins < 0) diffMins += 24 * 60; // wrap past midnight
                        if (diffMins <= 0) return null;
                        const hrs  = Math.floor(diffMins / 60);
                        const mins = diffMins % 60;
                        const label = hrs > 0
                          ? `${hrs} hour${hrs !== 1 ? "s" : ""}${mins > 0 ? ` ${mins} min` : ""}`
                          : `${mins} min`;
                        return (
                          <Box sx={{ mb: 1.5, display: "inline-flex", alignItems: "center", gap: 0.75, bgcolor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 1.5, px: 1.25, py: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 12, color: "#1d4ed8" }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8" }}>
                              Duration: {label}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: "#64748b" }}>
                              ({firstTime} → {lastTime})
                            </Typography>
                          </Box>
                        );
                      })()}
                      <Box sx={{ position: "relative" }}>
                        {selectedEvent.stops.map((stop, si) => {
                          const isFirst   = si === 0;
                          const isLast    = si === selectedEvent.stops.length - 1;
                          const isReturn  = stop.label === "Return Pickup" || stop.label === "Return Drop-off";
                          const isFirstReturn = stop.label === "Return Pickup" && si > 0 && selectedEvent.stops[si - 1].label !== "Return Pickup";
                          const stopColor = isReturn
                            ? (stop.label === "Return Pickup" ? "#2f3891" : "#db2777")
                            : isFirst ? "#16a34a" : isLast ? "#dc2626" : "#d97706";
                          const StopIcon  = isFirst ? CheckCircleOutlineIcon : (stop.label === "Return Drop-off" ? FlagIcon : isLast ? FlagIcon : LocationOnIcon);
                          return (
                            <React.Fragment key={si}>
                              {isFirstReturn && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 0.75 }}>
                                  <Box sx={{ flex: 1, height: 1, bgcolor: "#e2e8f0" }} />
                                  <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#2f3891", textTransform: "uppercase", letterSpacing: "0.08em" }}>Return Route</Typography>
                                  <Box sx={{ flex: 1, height: 1, bgcolor: "#e2e8f0" }} />
                                </Box>
                              )}
                              <Box sx={{ display: "flex", gap: 1.5 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 20, flexShrink: 0 }}>
                                  <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: `${stopColor}20`, border: `2px solid ${stopColor}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <StopIcon sx={{ fontSize: 11, color: stopColor }} />
                                  </Box>
                                  {si < selectedEvent.stops.length - 1 && (
                                    <Box sx={{ width: 2, flex: 1, minHeight: 20, bgcolor: "#e2e8f0", my: 0.25 }} />
                                  )}
                                </Box>
                                <Box sx={{ pb: si < selectedEvent.stops.length - 1 ? 1.5 : 0, flex: 1 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: stopColor, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                      {stop.label}
                                    </Typography>
                                    {stop.time && (
                                      <Chip label={stop.time} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: `${stopColor}15`, color: stopColor, border: "none" }} />
                                    )}
                                  </Box>
                                  <Typography sx={{ fontSize: 13, color: "#1e293b", fontWeight: 500, lineHeight: 1.4 }}>
                                    {stop.address || "—"}
                                  </Typography>
                                </Box>
                              </Box>
                            </React.Fragment>
                          );
                        })}
                      </Box>
                    </ModalSection>
                    <Divider sx={{ borderColor: "#f1f5f9" }} />
                  </>
                )}

                {!isDriver && (
                  <ModalSection title="Financial Summary" icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 14, color: "#2f3891" }} />}>
                    <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0", p: 1.5 }}>
                      <FinRow label="Grand Total" value={`$${selectedEvent.grandTotal.toFixed(2)}`} bold />
                      <FinRow label="Paid Amount"  value={`$${selectedEvent.amountPaid.toFixed(2)}`} color={selectedEvent.amountPaid > 0 ? "#16a34a" : "#94a3b8"} />
                      <Divider sx={{ my: 1, borderColor: "#e2e8f0" }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: balance === 0 ? "#16a34a" : "#dc2626" }}>Balance</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: balance === 0 ? "#16a34a" : "#dc2626" }}>${balance.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                  </ModalSection>
                )}

                {!selectedEvent.isQuote && !isDriver && <Divider sx={{ borderColor: "#f1f5f9" }} />}

                {!selectedEvent.isQuote && !isDriver && (() => {
                  const needed = Math.max(1, selectedEvent.busCount || 1);
                  const filledCount = assignDriverIds.filter(Boolean).length;
                  const allFilled = filledCount >= needed;
                  // Partial assignment allowed: enable as soon as at least one slot is filled.
                  const canSubmit = filledCount >= 1;
                  const driverBadge = filledCount === 0 ? "Unassigned" : filledCount < needed ? `${filledCount}/${needed} Assigned` : "Assigned";
                  const badgeColor = filledCount === 0 ? "#94a3b8" : filledCount < needed ? "#d97706" : "#16a34a";
                  return (
                    <ModalSection
                      title="Driver Assignment"
                      icon={<PersonAddIcon sx={{ fontSize: 14, color: "#2f3891" }} />}
                      badge={driverBadge}
                      badgeColor={badgeColor}>
                      {needed > 1 && (
                        <Box sx={{ mb: 1.5, px: 1.25, py: 0.75, bgcolor: "#eff6ff", borderRadius: 2, border: "1px dashed #bfdbfe", display: "flex", alignItems: "center", gap: 1 }}>
                          <DirectionsBusIcon sx={{ fontSize: 14, color: "#2563eb" }} />
                          <Typography sx={{ fontSize: 12, color: "#2563eb", fontWeight: 600 }}>
                            {needed} buses · {needed} drivers required
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                        {Array.from({ length: needed }).map((_, slotIdx) => {
                          const slotDriverId = assignDriverIds[slotIdx] || "";
                          const otherSelected = assignDriverIds.filter((id, i) => id && i !== slotIdx);
                          return (
                            <Box key={slotIdx}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
                                  Driver {slotIdx + 1}{needed > 1 ? ` (Bus ${slotIdx + 1})` : ""}
                                </Typography>
                                {slotDriverId && (
                                  <Box
                                    component="button"
                                    onClick={() => {
                                      const updated = [...assignDriverIds];
                                      while (updated.length < needed) updated.push("");
                                      updated[slotIdx] = "";
                                      setAssignDriverIds(updated);
                                    }}
                                    sx={{
                                      all: "unset",
                                      cursor: "pointer",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 0.25,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: "#dc2626",
                                      px: 0.75,
                                      py: 0.25,
                                      borderRadius: 1,
                                      "&:hover": { bgcolor: "#fef2f2" },
                                    }}
                                    title="Clear this slot"
                                  >
                                    ✕ Clear
                                  </Box>
                                )}
                              </Box>
                              <Select size="small" fullWidth value={slotDriverId}
                                onChange={(e) => {
                                  const updated = [...assignDriverIds];
                                  while (updated.length < needed) updated.push("");
                                  updated[slotIdx] = e.target.value;
                                  setAssignDriverIds(updated);
                                }}
                                displayEmpty
                                sx={{ fontSize: 13, borderRadius: 2, "& .MuiOutlinedInput-notchedOutline": { borderColor: slotDriverId ? "#2f3891" : "#e2e8f0" } }}>
                                <MenuItem value="" sx={{ fontSize: 13, color: "#94a3b8" }}>Select a driver…</MenuItem>
                                {allDrivers.map((dr: any) => {
                                  const isDuplicate = otherSelected.includes(String(dr.id));
                                  const avCheck = selectedEvent ? checkDriverAvailability(dr, selectedEvent.date, selectedEvent.rawSale?.departureDateTime ?? null, selectedEvent.rawSale?.returnDateTime ?? null) : { valid: true, reason: "" };
                                  return (
                                    <MenuItem key={dr.id} value={String(dr.id)} disabled={isDuplicate} sx={{ fontSize: 13, opacity: isDuplicate ? 0.4 : 1 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                            {cap(dr.driverFirstName)} {cap(dr.driverLastName)}
                                            {isDuplicate ? " (already assigned)" : ""}
                                          </Typography>
                                          {dr.driverMobileNum && <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{dr.driverMobileNum}</Typography>}
                                        </Box>
                                        {!isDuplicate && (avCheck.valid ? (
                                          <Box sx={{ px: 0.75, py: 0.2, bgcolor: "#dcfce7", borderRadius: 1, border: "1px solid #bbf7d0", flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#16a34a" }}>Available</Typography>
                                          </Box>
                                        ) : (
                                          <Tooltip title={avCheck.reason} arrow placement="left">
                                            <Box sx={{ px: 0.75, py: 0.2, bgcolor: "#fef3c7", borderRadius: 1, border: "1px solid #fde68a", flexShrink: 0 }}>
                                              <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#d97706" }}>Outside Hours</Typography>
                                            </Box>
                                          </Tooltip>
                                        ))}
                                      </Box>
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </Box>
                          );
                        })}
                      </Box>
                      <Button
                        fullWidth
                        disabled={isAssigning || !canSubmit}
                        onClick={handleAssignDriver}
                        startIcon={<SmsIcon sx={{ fontSize: 15 }} />}
                        sx={{ mt: 1.5, borderRadius: 2, textTransform: "none", fontSize: 12.5, fontWeight: 700, py: 0.875, bgcolor: canSubmit && !isAssigning ? "#2f3891" : "#e2e8f0", color: canSubmit && !isAssigning ? "#fff" : "#94a3b8", "&:hover": { bgcolor: "#2f3891" }, "&:disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
                        {!canSubmit
                          ? `Select at least 1 driver`
                          : !allFilled
                          ? `Assign ${filledCount}/${needed} Driver${filledCount > 1 ? "s" : ""} (rest later) →`
                          : needed > 1
                          ? `Review & Assign ${needed} Drivers →`
                          : "Review & Assign Driver →"}
                      </Button>
                      {selectedEvent.assignedDriverIds.length > 0 && (
                        <Button
                          fullWidth
                          variant="outlined"
                          disabled={isAssigning}
                          onClick={handleUnassignAll}
                          sx={{
                            mt: 1,
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: 12,
                            fontWeight: 700,
                            py: 0.625,
                            borderColor: "#fecaca",
                            color: "#b91c1c",
                            "&:hover": { bgcolor: "#fef2f2", borderColor: "#dc2626" },
                          }}
                        >
                          ✕ Unassign All Drivers
                        </Button>
                      )}
                      {canSubmit && !allFilled && (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75, textAlign: "center" }}>
                          You can come back later to assign the remaining {needed - filledCount} driver{needed - filledCount > 1 ? "s" : ""}.
                        </Typography>
                      )}
                      {allDrivers.length === 0 && (
                        <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 1 }}>No drivers available. Add drivers from Team Management.</Typography>
                      )}
                    </ModalSection>
                  );
                })()}

                {!isDriver && (
                  <Box sx={{ px: 2.5, pb: 2.5, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      onClick={() => { navigate(`/work-order/history/${selectedEvent.workOrderId}`); setSelectedEvent(null); }}
                      startIcon={<ReceiptLongOutlinedIcon sx={{ fontSize: 15 }} />}
                      sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700, px: 2.5, py: 0.875, bgcolor: "#2f3891", color: "#fff", "&:hover": { bgcolor: "#2f3891" } }}>
                      View Full Details
                    </Button>
                  </Box>
                )}
              </DialogContent>
            </>
          );
        })()}
      </Dialog>

      {/* ════════════════════════════════════════════════════════════════════
          SMS CONFIRMATION DIALOG
      ════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={smsConfirmOpen}
        onClose={() => setSmsConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        {/* Header */}
        <Box sx={{
          px: 2.5, py: 1.75,
          background: "linear-gradient(135deg, #2f3891 0%, #2563eb 100%)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SmsIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#fff", lineHeight: 1.2 }}>
                Confirm Assignment & SMS
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.75)" }}>
                Review and edit messages before sending
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setSmsConfirmOpen(false)}
            sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          {/* ── Client Message ─────────────────────────────────── */}
          <Box sx={{ mb: 2.5 }}>
            {/* Section header with toggle */}
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 1.5, py: 1, borderRadius: 2, mb: 0.75,
              bgcolor: smsClientEnabled ? "#eff6ff" : "#f8fafc",
              border: `1.5px solid ${smsClientEnabled ? "#bfdbfe" : "#e2e8f0"}`,
              transition: "all 0.2s",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <PhoneAndroidIcon sx={{ fontSize: 14, color: smsClientEnabled ? "#2f3891" : "#94a3b8" }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: smsClientEnabled ? "#374151" : "#94a3b8" }}>
                  Client Message
                </Typography>
                {selectedEvent && (
                  <Chip
                    label={selectedEvent.clientName}
                    size="small"
                    sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: "#eff6ff", color: "#2563eb", border: "none" }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: smsClientEnabled ? "#2563eb" : "#94a3b8", fontWeight: 600 }}>
                  {smsClientEnabled ? "ON" : "OFF"}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={smsClientEnabled}
                      onChange={(e) => setSmsClientEnabled(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#2563eb" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#2563eb" },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 11.5, fontWeight: 600, color: smsClientEnabled ? "#2563eb" : "#94a3b8" }}>Send to Client</Typography>}
                  labelPlacement="start"
                  sx={{ m: 0, gap: 0.5 }}
                />
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={clientSmsText}
              onChange={(e) => setClientSmsText(e.target.value)}
              disabled={!smsClientEnabled}
              sx={{
                opacity: smsClientEnabled ? 1 : 0.45,
                transition: "opacity 0.2s",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2, fontSize: 12.5, fontFamily: "monospace",
                  bgcolor: "#f8fafc", lineHeight: 1.6,
                  "& fieldset": { borderColor: "#e2e8f0" },
                  "&:hover fieldset": { borderColor: smsClientEnabled ? "#2f3891" : "#e2e8f0" },
                  "&.Mui-focused fieldset": { borderColor: "#2f3891" },
                },
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Button
                size="small"
                disabled={!smsClientEnabled}
                onClick={() => setClientSmsText(buildDefaultMessages().client)}
                sx={{ mt: 0.5, fontSize: 10.5, textTransform: "none", color: "#94a3b8", p: 0.25, "&:hover": { color: "#2f3891" } }}>
                Reset to default
              </Button>
              <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.5 }}>
                {clientSmsText.length} chars
              </Typography>
            </Box>
          </Box>

          {/* ── Driver Message ──────────────────────────────────── */}
          <Box>
            {/* Section header with toggle */}
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 1.5, py: 1, borderRadius: 2, mb: 0.75,
              bgcolor: smsDriverEnabled ? "#f0fdf4" : "#f8fafc",
              border: `1.5px solid ${smsDriverEnabled ? "#bbf7d0" : "#e2e8f0"}`,
              transition: "all 0.2s",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <DirectionsBusIcon sx={{ fontSize: 14, color: smsDriverEnabled ? "#16a34a" : "#94a3b8" }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: smsDriverEnabled ? "#374151" : "#94a3b8" }}>
                  Driver Message
                </Typography>
                {selectedEvent && (
                  <Chip
                    label={`${Math.max(1, selectedEvent.busCount || 1)} driver${Math.max(1, selectedEvent.busCount || 1) > 1 ? "s" : ""}`}
                    size="small"
                    sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: "#f0fdf4", color: "#16a34a", border: "none" }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: smsDriverEnabled ? "#16a34a" : "#94a3b8", fontWeight: 600 }}>
                  {smsDriverEnabled ? "ON" : "OFF"}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={smsDriverEnabled}
                      onChange={(e) => setSmsDriverEnabled(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#16a34a" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#16a34a" },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 11.5, fontWeight: 600, color: smsDriverEnabled ? "#16a34a" : "#94a3b8" }}>Send to Drivers</Typography>}
                  labelPlacement="start"
                  sx={{ m: 0, gap: 0.5 }}
                />
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={driverSmsText}
              onChange={(e) => setDriverSmsText(e.target.value)}
              disabled={!smsDriverEnabled}
              sx={{
                opacity: smsDriverEnabled ? 1 : 0.45,
                transition: "opacity 0.2s",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2, fontSize: 12.5, fontFamily: "monospace",
                  bgcolor: "#f8fafc", lineHeight: 1.6,
                  "& fieldset": { borderColor: "#e2e8f0" },
                  "&:hover fieldset": { borderColor: smsDriverEnabled ? "#2f3891" : "#e2e8f0" },
                  "&.Mui-focused fieldset": { borderColor: "#2f3891" },
                },
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Button
                size="small"
                disabled={!smsDriverEnabled}
                onClick={() => setDriverSmsText(buildDefaultMessages().driver)}
                sx={{ mt: 0.5, fontSize: 10.5, textTransform: "none", color: "#94a3b8", p: 0.25, "&:hover": { color: "#2f3891" } }}>
                Reset to default
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                {selectedEvent && Math.max(1, selectedEvent.busCount || 1) > 1 && (
                  <Typography sx={{ fontSize: 10.5, color: "#2f3891" }}>
                    Same message sent to all {Math.max(1, selectedEvent.busCount || 1)} drivers
                  </Typography>
                )}
                <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                  {driverSmsText.length} chars
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ── Both OFF warning ───────────────────────────────── */}
          {!smsClientEnabled && !smsDriverEnabled && (
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderRadius: 2, bgcolor: "#fefce8", border: "1.5px solid #fde68a", display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#92400e" }}>
                ⚠ No SMS will be sent. Enable at least one to notify recipients.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0, gap: 1 }}>
          <Button
            onClick={() => setSmsConfirmOpen(false)}
            sx={{
              textTransform: "none", borderRadius: 2, fontWeight: 600, px: 2,
              color: "#64748b", border: "1.5px solid #e2e8f0",
              "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" },
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAssign}
            disabled={isAssigning}
            startIcon={isAssigning
              ? <CircularProgress size={14} sx={{ color: "#fff" }} />
              : (smsClientEnabled || smsDriverEnabled)
                ? <SmsIcon sx={{ fontSize: 15 }} />
                : <CheckCircleIcon sx={{ fontSize: 15 }} />
            }
            sx={{
              flex: 1, textTransform: "none", borderRadius: 2, fontWeight: 700, py: 0.875,
              bgcolor: "#2f3891", color: "#fff",
              "&:hover": { bgcolor: "#2f3891" },
              "&:disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
            }}>
            {isAssigning
              ? "Assigning…"
              : (smsClientEnabled && smsDriverEnabled)
              ? "Confirm & Send SMS to Both"
              : smsClientEnabled
              ? "Confirm & Send SMS to Client"
              : smsDriverEnabled
              ? "Confirm & Send SMS to Drivers"
              : "Assign Drivers (No SMS)"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════════════════════════════════════════════════════════════
          LEG ASSIGN DRIVER DIALOG
      ════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={!!legAssignTrip}
        onClose={closeLegAssign}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {legAssignTrip && (() => {
          const badge = LEG_BADGE[legAssignTrip.legType];
          return (
            <>
              <Box sx={{
                px: 2.5, py: 1.75,
                background: "linear-gradient(135deg,#2f3891,#2563eb)",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <PersonAddIcon sx={{ color: "#fff", fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#fff" }}>
                        Assign Driver
                      </Typography>
                      <Box sx={{ px: 0.75, py: "2px", borderRadius: 1, bgcolor: badge.bg, fontSize: 10, fontWeight: 800, color: badge.color }}>
                        {badge.label}
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.75)" }}>
                      {legAssignTrip.clientName} · {legAssignTrip.quoteNo}
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={closeLegAssign}
                  sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              <DialogContent sx={{ pt: 2.5, pb: 1 }}>
                <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1.5 }}>
                  <strong>{legAssignTrip.date}</strong> at <strong>{legAssignTrip.time}</strong>
                  {legAssignTrip.stops[0] && ` · ${legAssignTrip.stops[0].address.split(",")[0]}`}
                </Typography>

                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#374151", mb: 0.75 }}>
                  Select Driver
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={legAssignDriverId}
                  onChange={(e) => setLegAssignDriverId(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: 13, borderRadius: 2, bgcolor: "#f8fafc" }}
                >
                  <MenuItem value="" sx={{ fontSize: 13, color: "#94a3b8" }}>— Choose a driver —</MenuItem>
                  {allDrivers.map((dr: any) => {
                    const name = `${cap(dr.driverFirstName)} ${cap(dr.driverLastName)}`.trim();
                    const isCurrentDriver = String(dr.id) === legAssignTrip.legDriverId;
                    return (
                      <MenuItem key={dr.id} value={String(dr.id)} sx={{ fontSize: 13 }}>
                        {name}{isCurrentDriver ? " (current)" : ""}
                      </MenuItem>
                    );
                  })}
                </Select>

                {legAssignTrip.legDriverName && (
                  <Typography sx={{ fontSize: 11, color: "#64748b", mt: 1 }}>
                    Currently assigned: <strong>{legAssignTrip.legDriverName}</strong>
                  </Typography>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0.5, gap: 1 }}>
                <Button onClick={closeLegAssign}
                  sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, color: "#64748b", border: "1.5px solid #e2e8f0", px: 2 }}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleConfirmLegAssign(false)}
                  disabled={!legAssignDriverId || isLegAssigning}
                  startIcon={isLegAssigning ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <CheckCircleIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    flex: 1, textTransform: "none", borderRadius: 2, fontWeight: 700, py: 0.875,
                    bgcolor: "#2f3891", color: "#fff", "&:hover": { bgcolor: "#2f3891" },
                    "&:disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                  }}>
                  {isLegAssigning ? "Assigning…" : "Assign"}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ════════════════════════════════════════════════════════════════════
          PER-LEG DIRECT DRIVER SMS DIALOG
      ════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={!!legSmsTrip}
        onClose={closeLegSms}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        {legSmsTrip && (
          <>
            <Box sx={{
              px: 2.5, py: 1.75,
              background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <SmsIcon sx={{ color: "#fff", fontSize: 17 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 14.5, color: "#fff" }}>Send SMS to Driver</Typography>
                  <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.78)" }}>
                    {legSmsTrip.legDriverName} · {legSmsTrip.clientName}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={closeLegSms}
                sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <DialogContent sx={{ pt: 2.5, pb: 1 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#374151", mb: 0.75 }}>
                Driver Phone
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={legSmsPhone}
                onChange={(e) => setLegSmsPhone(e.target.value)}
                placeholder="e.g. +1 555 000 0000"
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#374151", mb: 0.75 }}>
                Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={5}
                size="small"
                value={legSmsMessage}
                onChange={(e) => setLegSmsMessage(e.target.value)}
                placeholder="Type your message here…"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
              <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75, textAlign: "right" }}>
                {legSmsMessage.length} chars
              </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0.5, gap: 1 }}>
              <Button onClick={closeLegSms}
                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, color: "#64748b", border: "1.5px solid #e2e8f0", px: 2 }}>
                Cancel
              </Button>
              <Button
                onClick={handleSendLegSms}
                disabled={!legSmsPhone.trim() || !legSmsMessage.trim() || legSmsSending}
                startIcon={legSmsSending
                  ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                  : <SmsIcon sx={{ fontSize: 15 }} />
                }
                sx={{
                  flex: 1, textTransform: "none", borderRadius: 2, fontWeight: 700, py: 0.875,
                  bgcolor: "#2563eb", color: "#fff", "&:hover": { bgcolor: "#1d4ed8" },
                  "&:disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                }}>
                {legSmsSending ? "Sending…" : "Confirm & Send SMS to Driver"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ════════════════════════════════════════════════════════════════════
          SAME DRIVER FOR RETURN PROMPT
      ════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={!!sameDriverPrompt}
        onClose={() => setSameDriverPrompt(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {sameDriverPrompt && (
          <>
            <Box sx={{ px: 2.5, py: 1.75, bgcolor: "#f0fdf4", borderBottom: "1px solid #bbf7d0" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: "#15803d", fontSize: 22 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#15803d" }}>
                  Outbound Driver Assigned
                </Typography>
              </Box>
            </Box>
            <DialogContent sx={{ pt: 2, pb: 1 }}>
              <Typography sx={{ fontSize: 13.5, color: "#374151", mb: 1.5 }}>
                Use <strong>{sameDriverPrompt.driverName}</strong> for the return leg on{" "}
                <strong>{sameDriverPrompt.returnTrip.date}</strong> at{" "}
                <strong>{sameDriverPrompt.returnTrip.time}</strong>?
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                The return leg is currently unassigned. You can also assign a different driver manually.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0.5, gap: 1 }}>
              <Button onClick={() => setSameDriverPrompt(null)}
                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, color: "#64748b", border: "1.5px solid #e2e8f0", px: 2 }}>
                No, assign manually
              </Button>
              <Button
                onClick={handleSameDriverForReturn}
                startIcon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
                sx={{ flex: 1, textTransform: "none", borderRadius: 2, fontWeight: 700, py: 0.875, bgcolor: "#15803d", color: "#fff", "&:hover": { bgcolor: "#166534" } }}>
                Yes, same driver
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

// ─── Dispatch: Trip block on the time grid ────────────────────────────────────
const DispatchTripBlock = ({
  trip, left, rowH, tripW, isDragging, onDragStart, onDragEnd, onClick,
}: {
  trip: TripEvent;
  left: number;
  rowH?: number;
  tripW: number;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, trip: TripEvent) => void;
  onDragEnd: () => void;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}) => {
  const cfg      = STAGE[trip.stage] || STAGE.confirmed;
  const from     = trip.stops[0]?.address || "";
  const to       = trip.stops[trip.stops.length - 1]?.address || "";
  const routeTxt = from && to && from !== to ? `${from.split(",")[0]} → ${to.split(",")[0]}` : from || to || "";

  return (
    <Tooltip arrow placement="top"
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.25 }}>{trip.clientName}</Typography>
          <Typography sx={{ fontSize: 11 }}>{trip.service}</Typography>
          {routeTxt && <Typography sx={{ fontSize: 11 }}>📍 {routeTxt}</Typography>}
          <Typography sx={{ fontSize: 11, mt: 0.25 }}>{trip.time} · ${trip.grandTotal.toFixed(2)}</Typography>
        </Box>
      }>
      <Box
        draggable
        onDragStart={(e) => onDragStart(e, trip)}
        onDragEnd={onDragEnd}
        onClick={onClick}
        sx={{
          position: "absolute",
          left,
          top: "50%",
          transform: "translateY(-50%)",
          width: tripW,
          bgcolor: cfg.bg,
          border: `1.5px solid ${cfg.border}`,
          borderRadius: 1.5,
          px: 1, py: 0.5,
          cursor: "grab",
          userSelect: "none",
          opacity: isDragging ? 0.4 : 1,
          transition: "box-shadow 0.12s, opacity 0.12s",
          "&:hover": { boxShadow: `0 3px 10px ${cfg.border}88`, zIndex: 5 },
          "&:active": { cursor: "grabbing" },
          zIndex: 2,
          overflow: "hidden",
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.2 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.dot, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: cfg.color, lineHeight: 1 }}>{trip.time}</Typography>
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#1e293b", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {trip.clientName}
        </Typography>
        {routeTxt && (
          <Typography sx={{ fontSize: 9.5, color: "#64748b", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: 0.15 }}>
            {routeTxt}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

// ─── Dispatch: Unassigned trip chip ──────────────────────────────────────────
const TripChip = ({
  trip, isDragging, onDragStart, onDragEnd, onClick,
}: {
  trip: TripEvent;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, trip: TripEvent) => void;
  onDragEnd: () => void;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}) => {
  const cfg  = STAGE[trip.stage] || STAGE.confirmed;
  const from = trip.stops[0]?.address?.split(",")[0] || "";
  const to   = trip.stops[trip.stops.length - 1]?.address?.split(",")[0] || "";

  return (
    <Box
      draggable
      onDragStart={(e) => onDragStart(e, trip)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      sx={{
        flexShrink: 0, width: 155,
        bgcolor: "#fff", border: `1.5px solid ${cfg.border}`, borderRadius: 2,
        px: 1.25, py: 0.875, cursor: "grab", userSelect: "none",
        opacity: isDragging ? 0.35 : 1,
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
        transition: "box-shadow 0.12s, opacity 0.12s",
        "&:hover": { boxShadow: `0 3px 10px ${cfg.border}88` },
        "&:active": { cursor: "grabbing" },
      }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.dot, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: cfg.color }}>{trip.time}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {trip.clientName}
      </Typography>
      {(from || to) && (
        <Typography sx={{ fontSize: 10, color: "#64748b", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: 0.1 }}>
          {from}{from && to ? " → " : ""}{to}
        </Typography>
      )}
    </Box>
  );
};

// ─── Dispatch: Mobile simplified list ────────────────────────────────────────
const DispatchMobileList = ({
  tripsForDay, allDrivers, onOpenEvent,
}: {
  tripsForDay: TripEvent[];
  allDrivers?: any[];
  onOpenEvent: (trip: TripEvent) => void;
}) => (
  <Box>
    <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: 12 }}>
      Drag &amp; drop is available on desktop. Tap a trip to assign a driver.
    </Alert>
    {tripsForDay.length === 0 ? (
      <Typography sx={{ textAlign: "center", color: "#94a3b8", py: 4, fontSize: 14 }}>No trips for this day.</Typography>
    ) : (
      tripsForDay
        .sort((a, b) => (parseTimeMins(a.time) ?? 0) - (parseTimeMins(b.time) ?? 0))
        .map((trip) => {
          const cfg = STAGE[trip.stage] || STAGE.confirmed;
          return (
            <Paper key={trip.id} elevation={0} onClick={() => onOpenEvent(trip)}
              sx={{ mb: 1.25, borderRadius: 2, border: `1.5px solid ${cfg.border}`, p: 1.5, cursor: "pointer", "&:hover": { boxShadow: `0 2px 8px ${cfg.border}88` }, transition: "box-shadow 0.15s" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.dot }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: cfg.color }}>{trip.time}</Typography>
                <Chip label={cfg.label} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, ml: "auto" }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{trip.clientName}</Typography>
              <Typography sx={{ fontSize: 12, color: "#475569" }}>{trip.service}</Typography>
              {trip.assignedDriverNames.length > 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 13, color: "#16a34a" }} />
                  <Typography sx={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>{trip.assignedDriverNames.join(", ")}</Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <PersonAddIcon sx={{ fontSize: 13, color: "#f59e0b" }} />
                  <Typography sx={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>Tap to assign driver</Typography>
                </Box>
              )}
            </Paper>
          );
        })
    )}
  </Box>
);

// ─── Shared sub-components ────────────────────────────────────────────────────
const ModalSection = ({
  title, icon, badge, badgeColor, children,
}: {
  title: string; icon: React.ReactNode; badge?: string; badgeColor?: string; children: React.ReactNode;
}) => (
  <Box sx={{ px: 2.5, py: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
      <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.07em" }}>{title}</Typography>
      {badge && (
        <Box sx={{ ml: "auto", px: 1, py: 0.1, bgcolor: `${badgeColor}15`, borderRadius: 10, border: `1px solid ${badgeColor}40` }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: badgeColor }}>{badge}</Typography>
        </Box>
      )}
    </Box>
    {children}
  </Box>
);

const FinRow = ({ label, value, bold, color }: { label: string; value: string; bold?: boolean; color?: string }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
    <Typography sx={{ fontSize: 13, color: "#64748b", fontWeight: bold ? 700 : 400 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: color || (bold ? "#0f172a" : "#1e293b") }}>{value}</Typography>
  </Box>
);

export default BookingCalendar;
